import type { KnowledgeParticle } from '../types/domain';
import { safeExtractText } from '../utils/textHelper';
import {
  applyLearningEpisodeStreamRegistry,
  CORE_SUBJECT_CODES,
  getAvailableStreamsForSubject,
  getDefaultStreamForSubject,
  normalizeSubjectCode,
  streamUsesOpeningCard,
  type LearningEpisodeStream,
  type LearningEpisodeStreamRegistryEntry,
  type SubjectCode,
} from '../utils/subjectConfig';
import { getSubjectValueBindingRecord } from '../utils/subjectValueBinding';
import {
  getReviewProductDayKey,
  getReviewProductDayStartMs,
  isSameReviewProductDay,
} from '../utils/reviewProductDay';

import { apiClient } from './apiClient';
import { knowledgeService } from './knowledgeService';

export type ReviewParticleVector = {
  kpid: string;
  rsc: number;
};

export type ReviewFeedbackKind = 'proficient' | 'fuzzy' | 'forgotten';
export type ReviewLongPressIntentClass = 'learner_state' | 'callback' | 'quality';

export type LECReviewedParticle = {
  particleId: string;
  reviewedAt: string;
  feedback?: ReviewFeedbackKind | null;
};

export type LECLeadThoughtInputMode = 'text' | 'voice' | 'mixed';

export type LECLeadThoughtEntry = {
  text: string;
  updatedAt: string;
  inputMode?: LECLeadThoughtInputMode | null;
};

export type LearningEpisodeSessionHistoryEntry = {
  sessionId: string;
  subject: string;
  stream: string;
  algorithm: string | null;
  status: 'completed' | 'in_progress';
  particleCount: number;
  startedAt: string;
  completedAt: string | null;
  proficientCount: number;
  fuzzyCount: number;
  forgottenCount: number;
  completionScore: number | null;
  completionFeedback: string | null;
  reviewedFamilies: Array<{
    particleId: string;
    particleTitle: string | null;
    feedback: ReviewFeedbackKind | null;
    reviewedAt: string | null;
    score: number | null;
  }>;
};

export type LearningEpisodeFamilyFootprintEntry = {
  id: string;
  sessionId: string;
  sourceEpisodeId: string | null;
  subject: string;
  stream: string;
  particleId: string;
  particleTitle: string | null;
  particleQuestions?: unknown;
  particleSubject?: string | null;
  particleStatus?: string | null;
  particleTags?: string[] | null;
  particleMetadata?: Record<string, unknown> | null;
  feedback: ReviewFeedbackKind | null;
  summaryScore: number | null;
  summaryText: string | null;
  completedAt: string;
};

export type LearningEpisodeFamilyFootprintSnapshot = {
  total: number;
  rows: LearningEpisodeFamilyFootprintEntry[];
  pagination: { limit: number; offset: number };
};

export type LearningEpisodeRazBookmarkEntry = {
  eventId: string;
  userId: string;
  cardId: string;
  particleId: string;
  particleTitle?: string | null;
  title?: string | null;
  questionMain?: string | null;
  topicKey?: string | null;
  topicLabel?: string | null;
  subject: string;
  stream: string;
  sessionId?: string | null;
  cardKind?: string | null;
  streamDecorationKind?: string | null;
  familyIndex?: number | null;
  familyCardIndex?: number | null;
  familyCardCount?: number | null;
  savedAt: string;
};

export type LearningEpisodeRazBookmarkSnapshot = {
  total: number;
  rows: LearningEpisodeRazBookmarkEntry[];
  pagination: { limit: number; offset: number };
};

export type LearningEpisodeDifficultyAnnotationEntry = {
  eventId: string;
  userId: string;
  subject: string;
  stream: string;
  sessionId?: string | null;
  particleId?: string | null;
  particleTitle?: string | null;
  actionIntent: 'too-hard' | 'too-easy';
  actionLabel: string;
  cardKind?: string | null;
  streamDecorationKind?: string | null;
  occurredAt: string;
  createdAt: string;
};

export type LearningEpisodeDifficultyAnnotationSnapshot = {
  total: number;
  rows: LearningEpisodeDifficultyAnnotationEntry[];
  stats: {
    tooHard: number;
    tooEasy: number;
  };
  pagination: { limit: number; offset: number };
};

export type LearningEpisodeQualityFeedbackEntry = {
  eventId: string;
  userId: string;
  subject: string;
  stream: string;
  sessionId?: string | null;
  particleId?: string | null;
  particleTitle?: string | null;
  qualityReasonCodes: string[];
  qualityReasonLabel?: string | null;
  qualityReasonDescription?: string | null;
  noteText?: string | null;
  occurredAt: string;
  createdAt: string;
};

export type LearningEpisodeQualityFeedbackSnapshot = {
  total: number;
  reasonGroups: Array<{ reasonCode: string; count: number }>;
  rows: LearningEpisodeQualityFeedbackEntry[];
  pagination: { limit: number; offset: number };
};

export type LearningPositionSubjectSummary = {
  subject: string;
  footprintCount: number;
  latestCompletedAt: string;
};

export type LearningPositionSpineNodeStatus = 'idle' | 'visited' | 'proficient' | 'fuzzy' | 'forgotten';

export type LearningPositionSpineFamilyTrace = {
  particleId: string;
  particleTitle: string | null;
  feedback: ReviewFeedbackKind | null;
  summaryScore: number | null;
  completedAt: string;
};

export type LearningPositionSpineGraphNode = {
  nodeId: string;
  nodeNo: number;
  title: string;
  moduleKey: string | null;
  moduleLabel: string | null;
  firstLevelModuleKey?: string | null;
  firstLevelModuleLabel?: string | null;
  status: LearningPositionSpineNodeStatus;
  footprintCount: number;
  latestCompletedAt: string | null;
  families: LearningPositionSpineFamilyTrace[];
};

export type LearningPositionSpineUnmappedFootprint = LearningPositionSpineFamilyTrace & {
  unmappedReason:
    | 'no_binding'
    | 'incomplete_binding'
    | 'profile_not_found'
    | 'spine_not_found'
    | 'node_not_found';
  unmappedReasonLabel: string;
};

export type LearningPositionSpineGraph = {
  subject: string;
  subjectLabel: string;
  profileId: string | null;
  profileDisplayName: string | null;
  spineId: string | null;
  spineTitle: string | null;
  nodes: LearningPositionSpineGraphNode[];
  totalFootprintCount: number;
  mappedFootprintCount: number;
  unmappedFootprintCount: number;
  unmappedFootprints?: LearningPositionSpineUnmappedFootprint[];
  runtimeStatus?: 'ready' | 'unavailable' | 'empty';
  runtimeMessage?: string | null;
};

export type LearningPositionSpineCoverageNode = {
  nodeId: string;
  nodeNo: number;
  title: string;
  moduleKey: string | null;
  moduleLabel: string | null;
  firstLevelModuleKey?: string | null;
  firstLevelModuleLabel?: string | null;
  /** Bound knowledge particles on this trunk (histobar metric). */
  particleCount?: number;
  reviewedParticleCount: number;
};

export type LearningPositionSpineCoverageSummary = {
  subject: string;
  subjectLabel: string;
  profileId: string | null;
  profileDisplayName: string | null;
  spineId: string | null;
  spineTitle: string | null;
  nodes: LearningPositionSpineCoverageNode[];
  boundParticleCount?: number;
  mappedBoundParticleCount?: number;
  unmappedBoundParticleCount?: number;
  reviewedParticleCount: number;
  mappedReviewedParticleCount: number;
  unmappedReviewedParticleCount: number;
  runtimeStatus?: 'ready' | 'unavailable' | 'empty';
  runtimeMessage?: string | null;
};

export type LearningPositionSpineCoverageParticleItem = {
  id: string;
  title: string | null;
  reviewed: boolean;
};

export type LearningPositionSpineCoverageParticlePage = {
  subject: string;
  nodeId: string;
  nodeTitle: string;
  total: number;
  page: number;
  pageSize: number;
  items: LearningPositionSpineCoverageParticleItem[];
};

export type LearningPositionCoverageStat = {
  covered: number;
  total: number;
  rate: number;
};

export type LearningEpisodeParticleHistoryEntry = {
  sessionId: string;
  status: 'started' | 'completed';
  sourceEpisodeId?: string | null;
  subject?: string | null;
  algorithm?: LearningEpisodeAlgorithm | string | null;
  startedAt: string;
  completedAt?: string | null;
  updatedAt: string;
  isOpeningParticle: boolean;
  openingParticleId?: string | null;
  openingFeedback?: ReviewFeedbackKind | null;
  openingLeadThought?: LECLeadThoughtEntry | null;
  reviewedAt?: string | null;
  reviewFeedback?: ReviewFeedbackKind | null;
  leadThought?: LECLeadThoughtEntry | null;
  familySummary?: {
    score?: number | null;
    feedback?: string | null;
    submittedAt: string;
    printSubQuestionsExpanded?: boolean | null;
  } | null;
  completionScore?: number | null;
  completionFeedback?: string | null;
  completionSummary?: string | null;
};

export type LECDeckCursor = {
  particleId: string;
  cardId?: string | null;
  familyCardIndex?: number | null;
  updatedAt: string;
};

export type LECFeedbackSession = {
  id: string;
  userId: string;
  sourceEpisodeId?: string | null;
  subject?: string | null;
  stream?: string | null;
  algorithm?: LearningEpisodeAlgorithm | string | null;
  status: 'started' | 'completed';
  particleIds: string[];
  openingParticleId?: string | null;
  startedAt: string;
  openingFeedback?: ReviewFeedbackKind | null;
  reviewedParticles: LECReviewedParticle[];
  leadThoughtByParticleId: Record<string, LECLeadThoughtEntry>;
  familyFeedbackByParticleId: Record<string, {
    score?: number | null;
    feedback?: string | null;
    submittedAt: string;
    printSubQuestionsExpanded?: boolean | null;
  }>;
  deckCursor?: LECDeckCursor | null;
  completedAt?: string | null;
  completionScore?: number | null;
  completionFeedback?: string | null;
  completionSummary?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type LearningEpisodeAlgorithm =
  | 'breadth_first_learning_episode_v1'
  | 'depth_first_learning_episode_v1'
  | 'random_learning_episode_v1';

export type LearningMode = 'weekday' | 'holiday';

export type LearningEpisodeRequest = {
  algorithm?: LearningEpisodeAlgorithm;
  subject?: string;
  stream?: string;
  size?: number;
  recentAssetLimit?: number;
};

export type StreamSequenceLengthOverride = {
  weekdaySequenceLength: number;
  holidaySequenceLength: number;
};

export type StreamSequenceLengthMap = Partial<
  Record<SubjectCode, Partial<Record<string, StreamSequenceLengthOverride>>>
>;

export type LearningSequenceSettings = {
  learningMode: LearningMode;
  sequenceLength: number;
  weekdaySequenceLength: number;
  holidaySequenceLength: number;
  streamSequenceLengths?: StreamSequenceLengthMap;
  dailyAlgorithm: LearningEpisodeAlgorithm;
  sprintAlgorithm: LearningEpisodeAlgorithm;
  /** 品题加入复习列：打开后 LEC 才用 IPTTQ 题库替换源题 */
  includeIpttqInReviewSequence?: boolean;
  /**
   * 衍生内容超前年龄（岁）0～10，默认 1。
   * 相对实龄 A，内容适配区间约 [A+H-1, A+H+2]。
   */
  contentAgeAheadYears?: number;
};

type LearningEpisodeRefreshHints = {
  excludeParticleIds?: string[];
  excludeNodeIds?: string[];
};

function buildSwapExcludeParticleIds(plan: ReviewPlan | null | undefined): LearningEpisodeRefreshHints | undefined {
  if (!plan) {
    return undefined;
  }

  const source = Array.isArray(plan.episode.particles) && plan.episode.particles.length > 0
    ? plan.episode.particles
    : plan.particles;
  const excludeParticleIds = [
    ...source.map((particle) => particle.id),
    ...(Array.isArray(plan.episode.families)
      ? plan.episode.families.map((family) => family.particleId)
      : []),
  ].filter((id): id is string => typeof id === 'string' && id.length > 0);

  const uniqueExcludeParticleIds = [...new Set(excludeParticleIds)];
  return uniqueExcludeParticleIds.length > 0 ? { excludeParticleIds: uniqueExcludeParticleIds } : undefined;
}

function emitNewLearningEpisodeTraceSteps(
  emittedKeys: Set<string>,
  trace: LearningEpisodeTraceStep[] | undefined,
  onTrace?: (trace: LearningEpisodeTraceStep[]) => void,
) {
  if (!onTrace || !Array.isArray(trace) || trace.length === 0) {
    return;
  }

  const freshSteps = trace.filter((step) => {
    const key = String(step.key || `${step.label}:${step.detail}`);
    if (emittedKeys.has(key)) {
      return false;
    }
    emittedKeys.add(key);
    return true;
  });

  if (freshSteps.length > 0) {
    onTrace(freshSteps);
  }
}

export type LearningEpisodeTraceStep = {
  key: string;
  label: string;
  detail: string;
  tone: 'info' | 'success' | 'warning';
};

export type LearningEpisodeBuildStatus = 'queued' | 'running' | 'ready' | 'failed';

export type LearningEpisodeBuildSnapshot = {
  buildId: string;
  status: LearningEpisodeBuildStatus;
  request: LearningEpisodeRequest;
  trace: LearningEpisodeTraceStep[];
  episode?: LearningEpisodePayload | null;
  error?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type LearningEpisodeFamily = {
  particleId: string;
  particleTitle: string;
  nodeId: string;
  nodeTitle: string;
  nodeNo: number;
  moduleKey?: string;
};

export type LearningEpisodePayload = {
  id: string;
  title: string;
  algorithm: LearningEpisodeAlgorithm;
  algorithmLabel: string;
  subjectCode: string;
  subjectLabel: string;
  status: 'ready' | 'partial' | 'empty';
  streamKey?: string;
  providerKey?: string;
  providerKind?: 'deterministic' | 'emergent';
  providerCardEmissionMode?: 'static' | 'dynamic';
  providerHost?: 'appserver' | 'kpreactor';
  providerKernelKind?: 'internal_algorithm' | 'query_script' | 'hybrid';
  providerQueryScriptKey?: string | null;
  availabilityState?: 'ready' | 'no_particles' | 'provider_not_ready' | 'data_not_ready' | 'service_unavailable';
  emptyReasonCode?: string;
  targetFamilyCount: number;
  selectedFamilyCount: number;
  recentAssetCount: number;
  recentParticleCount: number;
  dominantProfileId?: string;
  dominantProfileName?: string;
  dominantSpineId?: string;
  dominantSpineTitle?: string;
  anchorNode?: {
    nodeId: string;
    nodeTitle: string;
    nodeNo: number;
  } | null;
  candidateNodeTitles: string[];
  summary: string;
  buildTrace: LearningEpisodeTraceStep[];
  families: LearningEpisodeFamily[];
  particles: KnowledgeParticle[];
  generatedAt: string;
};

export type LearningEpisodePlan = {
  kind: 'learning_episode';
  label: string;
  cacheKey: string;
  request: LearningEpisodeRequest;
  episode: LearningEpisodePayload;
  particles: KnowledgeParticle[];
  currentIndex: number;
  pendingCount: number;
  totalCount: number;
  source: 'generated' | 'restored';
  planId?: string | null;
  sessionId?: string | null;
  leadThoughtByParticleId: Record<string, LECLeadThoughtEntry>;
  familyFeedbackByParticleId: Record<string, {
    score?: number | null;
    feedback?: string | null;
    submittedAt: string;
    printSubQuestionsExpanded?: boolean | null;
  }>;
  deckCursor?: LECDeckCursor | null;
};

export type ReviewPlan = LearningEpisodePlan;

export type ReviewModeCard = {
  kind: 'learning_episode' | 'revision_family';
  label: string;
  description: string;
  pendingCount: number;
  enabled: boolean;
};

export type ReviewHomeSurface = {
  totalPendingCount: number;
  sequencePlan: ReviewPlan | null;
  modes: ReviewModeCard[];
  unavailableReason?: string;
  unavailableState?: LearningEpisodePayload['availabilityState'];
  unavailableCode?: string;
};

export type DailyReviewProgressEntry = {
  subject: string;
  stream: string;
  pendingCount: number;
  targetFamilyCount: number;
  reviewedCount: number;
  isComplete: boolean;
  isInfinite: boolean;
};

export type DailyReviewProgress = {
  totalPendingCount: number;
  bySubject: Partial<Record<SubjectCode, number>>;
  byStream: Partial<Record<SubjectCode, Partial<Record<string, number>>>>;
  entries: DailyReviewProgressEntry[];
};

export type StreamDailyBadge = {
  count: number;
  tone: 'pending' | 'complete';
  visible: boolean;
};

type LoadReviewSequenceInput = {
  userId: string;
  subjectFilter?: string;
  stream?: string;
  sequenceLengthOverride?: number;
  forceNewReview?: boolean;
  forceRefresh?: boolean;
  /** 传 false 时跳过复用当日 session，强制拉新计划（如日切后） */
  preferSession?: boolean;
  learningEpisode?: Partial<LearningEpisodeRequest>;
  learningEpisodeRefreshHints?: LearningEpisodeRefreshHints;
  onLearningEpisodeTrace?: (trace: LearningEpisodeTraceStep[]) => void;
  onBuildingProgress?: (progress: Extract<PlanReadyApiResponse, { status: 'building' }>) => void;
};

const LEARNING_EPISODE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const LEARNING_EPISODE_STREAM_REGISTRY_TTL_MS = 24 * 60 * 60 * 1000;
const DAILY_REVIEW_PROGRESS_TTL_MS = 30 * 60 * 1000;
const LEARNING_EPISODE_BUILD_POLL_INTERVAL_MS = 2_000;
const SESSION_HISTORY_TTL_MS = 5 * 60 * 1000;
const LEARNING_EPISODE_BUILD_TIMEOUT_MS = 45 * 1000;
const USE_PLAN_READY_STORE = String(import.meta.env.VITE_USE_PLAN_READY_STORE || 'true').trim().toLowerCase() !== 'false';
const DEFAULT_LEARNING_EPISODE_ALGORITHM: LearningEpisodeAlgorithm = 'breadth_first_learning_episode_v1';
const DEFAULT_LEARNING_EPISODE_SIZE = 5;
const DEFAULT_HOLIDAY_LEARNING_EPISODE_SIZE = 10;
const DEFAULT_LEARNING_SEQUENCE_SETTINGS: LearningSequenceSettings = {
  learningMode: 'weekday',
  sequenceLength: DEFAULT_LEARNING_EPISODE_SIZE,
  weekdaySequenceLength: DEFAULT_LEARNING_EPISODE_SIZE,
  holidaySequenceLength: DEFAULT_HOLIDAY_LEARNING_EPISODE_SIZE,
  dailyAlgorithm: 'breadth_first_learning_episode_v1',
  sprintAlgorithm: 'depth_first_learning_episode_v1',
  includeIpttqInReviewSequence: false,
  contentAgeAheadYears: 1,
};

function buildLearningEpisodeFallbackDisabledMessage(detail?: string) {
  return detail?.trim() || '当前没有可用的学习序列，请检查 Learning Episode 服务状态、入参与数据准备。';
}

function buildLearningEpisodeUserFacingUnavailableReason(code?: string, detail?: string) {
  switch (String(code || '').trim()) {
    case 'no_particles':
      return '当前学科还没有可用学习内容，暂时无法生成学习序列。';
    case 'no_recent_particles':
      return '最近这批学习内容还不足以整理出新的学习序列。';
    case 'no_candidate_particles':
      return '当前还没整理出新的学习序列，补充最近学习内容后再试即可。';
    case 'all_particles_excluded':
    case 'limited_particle_pool':
      return '知识颗粒素材池规模有限，无法保证生成全部有差异的学习序列。';
    case 'recent_bindings_unavailable':
      return '当前学习内容还在整理中，稍后再试即可。';
    case 'dominant_spine_missing':
    case 'dominant_spine_unresolved':
      return '当前还没整理出稳定的学习序列，稍后再试即可。';
    case 'runtime_profiles_unavailable':
    case 'provider_missing':
    case 'provider_disabled':
    case 'provider_stream_mismatch':
    case 'provider_subject_mismatch':
    case 'stream_placeholder':
      return '当前学习序列暂时不可用，请稍后再试。';
    case 'build_failed':
      return '学习序列生成失败，请稍后再试。';
    default:
      if (detail?.includes('素材池规模有限')) {
        return detail.trim();
      }
      return detail?.trim() || '当前没有可用的学习序列，请稍后再试。';
  }
}

const learningEpisodeSurfaceCache = new Map<string, {
  expiresAt: number;
  productDayKey: string;
  surface: ReviewHomeSurface;
}>();
const dailyReviewProgressCache = new Map<string, {
  expiresAt: number;
  productDayKey: string;
  progress: DailyReviewProgress;
}>();
const lastReviewProductDayByUserId = new Map<string, string>();
const REVIEW_PRODUCT_DAY_STORAGE_PREFIX = 'pf.reviewProductDay:';
const STREAM_PREWARM_COOLDOWN_MS = 5 * 60 * 1000;
const SIBLING_PREWARM_COOLDOWN_MS = 5 * 60 * 1000;
const streamPrewarmAtByKey = new Map<string, number>();
const siblingPrewarmAtByKey = new Map<string, number>();
const sessionHistoryCache = new Map<string, {
  expiresAt: number;
  limit: number;
  subject?: string;
  history: LearningEpisodeSessionHistoryEntry[];
}>();
const learningSequenceSettingsByUserId = new Map<string, LearningSequenceSettings>();
let learningEpisodeStreamRegistryExpiresAt = 0;
let learningEpisodeStreamRegistrySignature = '';

function extractPayloadError(payload: unknown, fallback: string) {
  if (!payload || typeof payload !== 'object') {
    return fallback;
  }

  const record = payload as { error?: unknown; message?: unknown };
  if (typeof record.error === 'string' && record.error.trim()) {
    return record.error;
  }
  if (typeof record.message === 'string' && record.message.trim()) {
    return record.message;
  }
  return fallback;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function requestMaybeEnvelope<T>(path: string, options?: { method?: 'GET' | 'POST'; body?: unknown }) {
  const payload = await apiClient.requestJson<any>(path, {
    method: options?.method,
    body: options?.body,
    auth: true,
  });

  if (payload && typeof payload === 'object' && Object.prototype.hasOwnProperty.call(payload, 'success')) {
    if (!payload.success) {
      throw new Error(extractPayloadError(payload, '请求失败'));
    }
    return (payload.data ?? null) as T;
  }

  return payload as T;
}

function buildLearningEpisodeStreamRegistrySignature(entries: LearningEpisodeStreamRegistryEntry[]) {
  return JSON.stringify(entries.map((entry) => ({
    streamKey: String(entry?.streamKey || '').trim(),
    status: String(entry?.status || '').trim(),
    description: String(entry?.description || '').trim(),
    supportedSubjectCodes: Array.isArray(entry?.supportedSubjectCodes) ? entry.supportedSubjectCodes.slice() : [],
    subjectOrderMap: entry?.subjectOrderMap && typeof entry.subjectOrderMap === 'object' ? { ...entry.subjectOrderMap } : {},
    decorationKeys: Array.isArray(entry?.decorationKeys) ? entry.decorationKeys.slice() : [],
  })));
}

async function syncLearningEpisodeStreamRegistry(options?: { forceRefresh?: boolean }) {
  const now = Date.now();
  if (!options?.forceRefresh && learningEpisodeStreamRegistryExpiresAt > now) {
    return;
  }

  try {
    const entries = await requestMaybeEnvelope<LearningEpisodeStreamRegistryEntry[]>('/api/review/learning-episode-streams', {
      method: 'GET',
    });
    if (Array.isArray(entries) && entries.length > 0) {
      const nextSignature = buildLearningEpisodeStreamRegistrySignature(entries);
      applyLearningEpisodeStreamRegistry(entries);
      if (nextSignature !== learningEpisodeStreamRegistrySignature) {
        learningEpisodeStreamRegistrySignature = nextSignature;
        learningEpisodeSurfaceCache.clear();
      }
    }
    learningEpisodeStreamRegistryExpiresAt = now + LEARNING_EPISODE_STREAM_REGISTRY_TTL_MS;
  } catch (error) {
    console.debug('[reviewService] syncLearningEpisodeStreamRegistry failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

function normalizeSubjectName(value?: string | null) {
  return String(value || '').trim().toLowerCase();
}

function normalizeEpisodeSubject(value?: string | null) {
  const normalized = normalizeSubjectName(value);
  return normalized || 'math';
}

function normalizeLearningEpisodeAlgorithm(value?: string | null): LearningEpisodeAlgorithm {
  if (value === 'random_learning_episode_v1') {
    return 'random_learning_episode_v1';
  }
  return value === 'depth_first_learning_episode_v1'
    ? 'depth_first_learning_episode_v1'
    : DEFAULT_LEARNING_EPISODE_ALGORITHM;
}

function normalizeLearningMode(value?: string | null): LearningMode {
  return value === 'holiday' ? 'holiday' : 'weekday';
}

function inferProviderCardEmissionMode(input: Pick<LearningEpisodePayload, 'providerCardEmissionMode' | 'providerKey' | 'algorithm'>) {
  if (input.providerCardEmissionMode === 'dynamic' || input.providerCardEmissionMode === 'static') {
    return input.providerCardEmissionMode;
  }

  if (String(input.providerKey || '').trim() === 'random_v1' || input.algorithm === 'random_learning_episode_v1') {
    return 'dynamic' as const;
  }

  return undefined;
}

function normalizeLearningEpisodePayload(episode: LearningEpisodePayload): LearningEpisodePayload {
  const providerCardEmissionMode = inferProviderCardEmissionMode(episode);
  if (providerCardEmissionMode === episode.providerCardEmissionMode) {
    return episode;
  }

  return {
    ...episode,
    providerCardEmissionMode,
  };
}

function normalizeSequenceLength(value?: number | null) {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return DEFAULT_LEARNING_EPISODE_SIZE;
  }

  return Math.max(1, Math.min(12, Math.floor(numeric)));
}

export function buildLearningEpisodeRequest(input?: Partial<LearningEpisodeRequest>): LearningEpisodeRequest {
  const size = typeof input?.size === 'number' && Number.isFinite(input.size) && input.size > 0
    ? Math.floor(input.size)
    : DEFAULT_LEARNING_EPISODE_SIZE;
  const recentAssetLimit = typeof input?.recentAssetLimit === 'number' && Number.isFinite(input.recentAssetLimit) && input.recentAssetLimit > 0
    ? Math.floor(input.recentAssetLimit)
    : undefined;

  return {
    algorithm: input?.algorithm ? normalizeLearningEpisodeAlgorithm(input.algorithm) : undefined,
    subject: normalizeEpisodeSubject(input?.subject),
    stream: input?.stream || undefined,
    size,
    recentAssetLimit,
  };
}

function normalizeStreamSequenceLengthOverride(
  value: unknown,
  fallbackWeekday: number,
  fallbackHoliday: number,
): StreamSequenceLengthOverride {
  const source = value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};

  return {
    weekdaySequenceLength: normalizeSequenceLength(
      typeof source.weekdaySequenceLength === 'number' ? source.weekdaySequenceLength : fallbackWeekday,
    ),
    holidaySequenceLength: normalizeSequenceLength(
      typeof source.holidaySequenceLength === 'number' ? source.holidaySequenceLength : fallbackHoliday,
    ),
  };
}

function normalizeStreamSequenceLengths(
  value: unknown,
  fallbackWeekday: number,
  fallbackHoliday: number,
): StreamSequenceLengthMap | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const source = value as Record<string, unknown>;
  const next: StreamSequenceLengthMap = {};

  for (const subject of CORE_SUBJECT_CODES) {
    const subjectRaw = source[subject];
    if (!subjectRaw || typeof subjectRaw !== 'object' || Array.isArray(subjectRaw)) {
      continue;
    }

    const streamMap: Partial<Record<string, StreamSequenceLengthOverride>> = {};
    for (const [streamKey, streamRaw] of Object.entries(subjectRaw as Record<string, unknown>)) {
      const normalizedStreamKey = String(streamKey || '').trim();
      if (!normalizedStreamKey) {
        continue;
      }
      streamMap[normalizedStreamKey] = normalizeStreamSequenceLengthOverride(streamRaw, fallbackWeekday, fallbackHoliday);
    }

    if (Object.keys(streamMap).length > 0) {
      next[subject] = streamMap;
    }
  }

  return Object.keys(next).length > 0 ? next : undefined;
}

function resolveSequenceLengthForMode(settings: Pick<LearningSequenceSettings, 'learningMode' | 'weekdaySequenceLength' | 'holidaySequenceLength'>) {
  return settings.learningMode === 'holiday'
    ? settings.holidaySequenceLength
    : settings.weekdaySequenceLength;
}

export function resolveSequenceLengthForSubjectStream(
  settings: Pick<LearningSequenceSettings, 'learningMode' | 'weekdaySequenceLength' | 'holidaySequenceLength' | 'streamSequenceLengths'>,
  subject: SubjectCode,
  stream: string,
): number {
  const normalizedStream = String(stream || '').trim();
  const override = normalizedStream
    ? settings.streamSequenceLengths?.[subject]?.[normalizedStream]
    : undefined;

  if (override) {
    return settings.learningMode === 'holiday'
      ? override.holidaySequenceLength
      : override.weekdaySequenceLength;
  }

  return resolveSequenceLengthForMode(settings);
}

function resolveAlgorithmForMode(settings: Pick<LearningSequenceSettings, 'learningMode' | 'dailyAlgorithm' | 'sprintAlgorithm'>) {
  return settings.learningMode === 'holiday'
    ? settings.sprintAlgorithm
    : settings.dailyAlgorithm;
}

function normalizeContentAgeAheadYears(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 1;
  return Math.min(10, Math.max(0, Math.round(numeric)));
}

function normalizeLearningSequenceSettings(input?: Partial<LearningSequenceSettings>): LearningSequenceSettings {
  const learningMode = normalizeLearningMode(input?.learningMode);
  const weekdaySequenceLength = normalizeSequenceLength(input?.weekdaySequenceLength ?? input?.sequenceLength ?? DEFAULT_LEARNING_EPISODE_SIZE);
  const holidaySequenceLength = normalizeSequenceLength(input?.holidaySequenceLength ?? DEFAULT_HOLIDAY_LEARNING_EPISODE_SIZE);
  const dailyAlgorithm = normalizeLearningEpisodeAlgorithm(input?.dailyAlgorithm);
  const sprintAlgorithm = normalizeLearningEpisodeAlgorithm(input?.sprintAlgorithm === 'depth_first_learning_episode_v1'
    ? input.sprintAlgorithm
    : 'depth_first_learning_episode_v1');

  return {
    learningMode,
    weekdaySequenceLength,
    holidaySequenceLength,
    streamSequenceLengths: normalizeStreamSequenceLengths(input?.streamSequenceLengths, weekdaySequenceLength, holidaySequenceLength),
    dailyAlgorithm,
    sprintAlgorithm,
    sequenceLength: learningMode === 'holiday' ? holidaySequenceLength : weekdaySequenceLength,
    includeIpttqInReviewSequence: input?.includeIpttqInReviewSequence === true,
    contentAgeAheadYears: normalizeContentAgeAheadYears(
      input?.contentAgeAheadYears ?? DEFAULT_LEARNING_SEQUENCE_SETTINGS.contentAgeAheadYears,
    ),
  };
}

function readLearningSequenceSettings(userId: string): LearningSequenceSettings {
  const stored = learningSequenceSettingsByUserId.get(userId);
  if (!stored) {
    return DEFAULT_LEARNING_SEQUENCE_SETTINGS;
  }

  return normalizeLearningSequenceSettings(stored);
}

export function getLearningSequenceSettings(userId: string): LearningSequenceSettings {
  return {
    ...readLearningSequenceSettings(userId),
  };
}

export function updateLearningSequenceSettings(userId: string, updates: Partial<LearningSequenceSettings>): LearningSequenceSettings {
  const current = readLearningSequenceSettings(userId);
  const nextLearningMode = normalizeLearningMode(updates.learningMode ?? current.learningMode);
  let weekdaySequenceLength = normalizeSequenceLength(updates.weekdaySequenceLength ?? current.weekdaySequenceLength);
  let holidaySequenceLength = normalizeSequenceLength(updates.holidaySequenceLength ?? current.holidaySequenceLength);

  if (typeof updates.sequenceLength === 'number' && Number.isFinite(updates.sequenceLength)) {
    if (nextLearningMode === 'holiday') {
      holidaySequenceLength = normalizeSequenceLength(updates.sequenceLength);
    } else {
      weekdaySequenceLength = normalizeSequenceLength(updates.sequenceLength);
    }
  }

  const nextSettings = normalizeLearningSequenceSettings({
    learningMode: nextLearningMode,
    weekdaySequenceLength,
    holidaySequenceLength,
    streamSequenceLengths: updates.streamSequenceLengths ?? current.streamSequenceLengths,
    dailyAlgorithm: updates.dailyAlgorithm ?? current.dailyAlgorithm,
    sprintAlgorithm: updates.sprintAlgorithm ?? current.sprintAlgorithm,
    includeIpttqInReviewSequence: updates.includeIpttqInReviewSequence ?? current.includeIpttqInReviewSequence,
    contentAgeAheadYears: updates.contentAgeAheadYears ?? current.contentAgeAheadYears,
  });

  learningSequenceSettingsByUserId.set(userId, nextSettings);
  return {
    ...nextSettings,
  };
}

export function buildConfiguredLearningEpisodeRequest(userId: string, input?: Partial<LearningEpisodeRequest>): LearningEpisodeRequest {
  const settings = readLearningSequenceSettings(userId);
  const subject = normalizeSubjectCode(input?.subject);
  const stream = String(input?.stream || '').trim();
  const activeSequenceLength = stream
    ? resolveSequenceLengthForSubjectStream(settings, subject, stream)
    : resolveSequenceLengthForMode(settings);

  return buildLearningEpisodeRequest({
    algorithm: input?.algorithm,
    subject: input?.subject,
    stream: input?.stream,
    size: input?.size ?? activeSequenceLength,
    recentAssetLimit: input?.recentAssetLimit,
  });
}

function buildEpisodeSubjectLabel(subject?: string | null) {
  const normalized = normalizeSubjectName(subject);
  if (matchesMathSubject(normalized)) {
    return '小学数学';
  }
  if (normalized === 'yuwen' || normalized.includes('语文')) {
    return '语文';
  }
  if (normalized === 'english' || normalized.includes('英语')) {
    return '英语';
  }
  return String(subject || '学习序列').trim() || '学习序列';
}

function buildLearningEpisodeAlgorithmLabel(algorithm: LearningEpisodeAlgorithm) {
  if (algorithm === 'random_learning_episode_v1') {
    return 'Random LearningEpisode';
  }

  return algorithm === 'depth_first_learning_episode_v1'
    ? 'Depth First LearningEpisode'
    : 'Breadth First LearningEpisode';
}

function matchesMathSubject(value?: string | null) {
  const normalized = normalizeSubjectName(value);
  if (!normalized) {
    return false;
  }

  return normalized.includes('math') || normalized.includes('数学');
}

function readBindingText(record: Record<string, unknown> | undefined, keys: string[]) {
  if (!record) {
    return '';
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
}

function readBindingNumber(record: Record<string, unknown> | undefined, keys: string[]) {
  if (!record) {
    return null;
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string' && value.trim()) {
      const numeric = Number(value);
      if (Number.isFinite(numeric)) {
        return numeric;
      }
    }
  }

  return null;
}

function buildParticlePreviewTitle(particle: KnowledgeParticle, index: number) {
  const preview = [particle.title, safeExtractText(particle.questions), particle.content, safeExtractText(particle.answers)]
    .map((value) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : ''))
    .find(Boolean) || `知识颗粒 ${index + 1}`;
  return preview.length > 36 ? `${preview.slice(0, 36)}...` : preview;
}

export function countPendingFamiliesInSession(
  session: Pick<LECFeedbackSession, 'particleIds' | 'familyFeedbackByParticleId' | 'reviewedParticles'>,
): number {
  const particleIds = Array.isArray(session.particleIds)
    ? session.particleIds.map((id) => String(id || '').trim()).filter(Boolean)
    : [];
  if (!particleIds.length) {
    return 0;
  }

  const familyFeedback = session.familyFeedbackByParticleId || {};
  const reviewedParticleIds = new Set<string>();

  for (const [particleId, entry] of Object.entries(familyFeedback)) {
    if (typeof entry.score === 'number' || Boolean(String(entry.feedback || '').trim())) {
      reviewedParticleIds.add(particleId);
    }
  }

  for (const record of session.reviewedParticles || []) {
    const normalizedParticleId = String(record.particleId || '').trim();
    if (!normalizedParticleId || !record.feedback) {
      continue;
    }
    reviewedParticleIds.add(normalizedParticleId);
  }

  if (!reviewedParticleIds.size) {
    return particleIds.length;
  }

  return particleIds.filter((particleId) => !reviewedParticleIds.has(particleId)).length;
}

function getLocalDayStartMs(now: Date = new Date()): number {
  return getReviewProductDayStartMs(now);
}

function hasMeaningfulFamilyReviewEntry(entry: {
  score?: number | null;
  feedback?: string | null;
} | null | undefined): boolean {
  return typeof entry?.score === 'number' || Boolean(String(entry?.feedback || '').trim());
}

export function countReviewedFamiliesInSession(
  session: Pick<LECFeedbackSession, 'familyFeedbackByParticleId' | 'reviewedParticles'>,
  options?: { sinceMs?: number },
): number {
  const sinceMs = options?.sinceMs ?? getLocalDayStartMs();
  const counted = new Set<string>();

  for (const [particleId, entry] of Object.entries(session.familyFeedbackByParticleId || {})) {
    const normalizedParticleId = String(particleId || '').trim();
    if (!normalizedParticleId || !hasMeaningfulFamilyReviewEntry(entry)) {
      continue;
    }

    const submittedAt = entry?.submittedAt ? new Date(entry.submittedAt).getTime() : Number.NaN;
    if (Number.isFinite(submittedAt) && submittedAt >= sinceMs) {
      counted.add(normalizedParticleId);
    }
  }

  for (const record of session.reviewedParticles || []) {
    const normalizedParticleId = String(record.particleId || '').trim();
    if (!normalizedParticleId || counted.has(normalizedParticleId)) {
      continue;
    }

    if (!record.feedback) {
      continue;
    }

    const reviewedAt = new Date(record.reviewedAt).getTime();
    if (Number.isFinite(reviewedAt) && reviewedAt >= sinceMs) {
      counted.add(normalizedParticleId);
    }
  }

  return counted.size;
}

export function findDailyReviewProgressEntry(
  progress: DailyReviewProgress | null | undefined,
  subject: string,
  stream: string,
): DailyReviewProgressEntry | null {
  const normalizedSubject = normalizeSubjectCode(subject);
  const normalizedStream = String(stream || '').trim();
  if (!normalizedStream) {
    return null;
  }

  return (progress?.entries || []).find(
    (entry) => normalizeSubjectCode(entry.subject) === normalizedSubject && entry.stream === normalizedStream,
  ) ?? null;
}

export function resolveStreamDailyBadge(entry: DailyReviewProgressEntry | null | undefined): StreamDailyBadge {
  if (!entry) {
    return { count: 0, tone: 'pending', visible: false };
  }

  if (entry.isInfinite) {
    if (entry.isComplete) {
      return { count: 0, tone: 'complete', visible: true };
    }
    if (entry.pendingCount > 0) {
      return { count: entry.pendingCount, tone: 'pending', visible: true };
    }
    return { count: 0, tone: 'complete', visible: false };
  }

  if (entry.isComplete) {
    return {
      count: 0,
      tone: 'complete',
      visible: true,
    };
  }

  const pendingCount = Math.max(0, entry.pendingCount ?? 0);
  return {
    count: pendingCount,
    tone: 'pending',
    visible: pendingCount > 0,
  };
}

function isShuffleInfiniteStream(stream: string) {
  return String(stream || '').trim() === 'shuffle';
}

function computeDailyReviewPendingForStream(
  targetFamilyCount: number,
  session: Pick<LECFeedbackSession, 'familyFeedbackByParticleId' | 'reviewedParticles' | 'particleIds'> | null | undefined,
  stream: string,
  options?: { todayFootprintCount?: number },
): { pendingCount: number; targetFamilyCount: number; reviewedCount: number; isComplete: boolean } {
  const normalizedTarget = normalizeSequenceLength(targetFamilyCount);
  const todayFootprintCount = Math.max(0, options?.todayFootprintCount ?? 0);
  const sessionReviewedCount = session ? countReviewedFamiliesInSession(session) : 0;
  const reviewedCount = Math.max(sessionReviewedCount, todayFootprintCount);

  return {
    pendingCount: Math.max(0, normalizedTarget - reviewedCount),
    targetFamilyCount: normalizedTarget,
    reviewedCount,
    isComplete: reviewedCount >= normalizedTarget,
  };
}

function mergeDailyReviewProgressEntries(entries: DailyReviewProgressEntry[]): DailyReviewProgress {
  const bySubject: Partial<Record<SubjectCode, number>> = {};
  const byStream: Partial<Record<SubjectCode, Partial<Record<string, number>>>> = {};
  const mergedEntries = new Map<string, DailyReviewProgressEntry>();

  for (const entry of entries) {
    const subject = normalizeSubjectCode(entry.subject);
    const stream = String(entry.stream || '').trim();
    if (!stream) {
      continue;
    }

    const key = `${subject}:${stream}`;
    const existing = mergedEntries.get(key);
    const pendingCount = Math.max(existing?.pendingCount ?? 0, entry.pendingCount);
    const targetFamilyCount = Math.max(existing?.targetFamilyCount ?? 0, entry.targetFamilyCount);
    const reviewedCount = Math.max(existing?.reviewedCount ?? 0, entry.reviewedCount ?? 0);
    const isComplete = reviewedCount >= targetFamilyCount && targetFamilyCount > 0;
    mergedEntries.set(key, {
      subject,
      stream,
      pendingCount: isComplete ? 0 : Math.max(0, targetFamilyCount - reviewedCount),
      targetFamilyCount,
      reviewedCount,
      isComplete: isShuffleInfiniteStream(stream)
        ? (entry.isComplete ?? (reviewedCount >= targetFamilyCount && targetFamilyCount > 0))
        : isComplete,
      isInfinite: isShuffleInfiniteStream(stream),
    });
  }

  for (const entry of mergedEntries.values()) {
    if (entry.pendingCount <= 0) {
      continue;
    }

    const subject = normalizeSubjectCode(entry.subject);
    if (!byStream[subject]) {
      byStream[subject] = {};
    }
    byStream[subject]![entry.stream] = Math.max(byStream[subject]![entry.stream] || 0, entry.pendingCount);

    if (isShuffleInfiniteStream(entry.stream)) {
      continue;
    }

    bySubject[subject] = (bySubject[subject] || 0) + entry.pendingCount;
  }

  const totalPendingCount = Object.values(bySubject).reduce((sum, count) => sum + (count || 0), 0);
  const maxConfiguredTotal = Array.from(mergedEntries.values())
    .filter((entry) => !entry.isInfinite)
    .reduce((sum, entry) => sum + Math.max(0, entry.targetFamilyCount || 0), 0);

  return {
    totalPendingCount: maxConfiguredTotal > 0 ? Math.min(totalPendingCount, maxConfiguredTotal) : totalPendingCount,
    bySubject,
    byStream,
    entries: Array.from(mergedEntries.values()),
  };
}

export function isDailyReviewComplete(progress: DailyReviewProgress | null | undefined): boolean {
  if (!progress) {
    return false;
  }

  const finiteTargets = (progress.entries || []).filter(
    (entry) => !entry.isInfinite && entry.targetFamilyCount > 0,
  );

  if (finiteTargets.length === 0) {
    return progress.totalPendingCount <= 0;
  }

  return finiteTargets.every((entry) => entry.isComplete);
}

async function collectDailyReviewProgressFromSessionsAndCache(userId: string): Promise<DailyReviewProgress> {
  const settings = readLearningSequenceSettings(userId);
  const entries: DailyReviewProgressEntry[] = [];

  await Promise.all(
    CORE_SUBJECT_CODES.flatMap((subject) => getAvailableStreamsForSubject(subject).map(async (stream) => {
      const configuredLength = resolveSequenceLengthForSubjectStream(settings, subject, stream);
      let session: Pick<LECFeedbackSession, 'familyFeedbackByParticleId' | 'reviewedParticles' | 'particleIds'> | null = null;

      try {
        session = await getActiveLearningEpisodeSession(userId, subject, stream);
      } catch {
        // Best effort per stream.
      }

      const { pendingCount, targetFamilyCount, reviewedCount, isComplete } = computeDailyReviewPendingForStream(
        configuredLength,
        session,
        stream,
      );

      entries.push({
        subject,
        stream,
        pendingCount,
        targetFamilyCount,
        reviewedCount,
        isComplete,
        isInfinite: isShuffleInfiniteStream(stream),
      });
    })),
  );

  return mergeDailyReviewProgressEntries(entries);
}

function isLearningEpisodePlanReadyForStream(
  plan: LearningEpisodePlan,
  requiredSize: number,
): boolean {
  if (!plan.pendingCount || plan.particles.length <= 0) {
    return false;
  }

  if (plan.episode.providerCardEmissionMode === 'dynamic') {
    return true;
  }

  if (plan.source === 'restored' || plan.sessionId) {
    return plan.episode.status !== 'empty';
  }

  if (plan.episode.status === 'ready' || plan.episode.status === 'partial') {
    return true;
  }

  return plan.episode.selectedFamilyCount >= requiredSize;
}

function buildReviewModes(sequencePlan: ReviewPlan | null, unavailableReason?: string): ReviewModeCard[] {
  const reviewDescription = sequencePlan?.episode.summary || unavailableReason || '按最近素材和学科主干构造新的学习序列。';

  return [
    {
      kind: 'learning_episode',
      label: '学习序列',
      description: reviewDescription,
      pendingCount: sequencePlan?.pendingCount ?? 0,
      enabled: Boolean(sequencePlan && sequencePlan.pendingCount > 0),
    },
    {
      kind: 'revision_family',
      label: '节假日模式',
      description: '切到节假日模式后，系统会围绕最近主干和相邻节点，生成更长的深刷序列。',
      pendingCount: 0,
      enabled: false,
    },
  ];
}

function buildLearningEpisodeCacheKey(input: {
  userId: string;
  request: LearningEpisodeRequest;
}) {
  const request = buildLearningEpisodeRequest(input.request);
  return [
    input.userId,
    request.stream || '',
    request.algorithm,
    request.subject,
    request.size,
    request.recentAssetLimit || 0,
  ].join(':');
}

function cloneReviewHomeSurface(surface: ReviewHomeSurface): ReviewHomeSurface {
  return {
    ...surface,
    sequencePlan: surface.sequencePlan
      ? {
        ...surface.sequencePlan,
        particles: surface.sequencePlan.particles.slice(),
      }
      : null,
    modes: surface.modes.map((mode) => ({ ...mode })),
  };
}

function getCachedLearningEpisodeSurface(cacheKey: string) {
  const cached = learningEpisodeSurfaceCache.get(cacheKey);
  if (!cached) {
    return null;
  }

  if (cached.expiresAt <= Date.now() || cached.productDayKey !== getReviewProductDayKey()) {
    learningEpisodeSurfaceCache.delete(cacheKey);
    return null;
  }

  return cloneReviewHomeSurface(cached.surface);
}

function setCachedLearningEpisodeSurface(cacheKey: string, surface: ReviewHomeSurface) {
  learningEpisodeSurfaceCache.set(cacheKey, {
    expiresAt: Date.now() + LEARNING_EPISODE_CACHE_TTL_MS,
    productDayKey: getReviewProductDayKey(),
    surface: cloneReviewHomeSurface(surface),
  });
}

function clearCachedLearningEpisodeSurface(cacheKey: string) {
  learningEpisodeSurfaceCache.delete(cacheKey);
}

function getCachedDailyReviewProgress(userId: string) {
  const cached = dailyReviewProgressCache.get(userId);
  if (!cached) {
    return null;
  }
  if (cached.expiresAt <= Date.now() || cached.productDayKey !== getReviewProductDayKey()) {
    dailyReviewProgressCache.delete(userId);
    return null;
  }
  return cached.progress;
}

function setCachedDailyReviewProgress(userId: string, progress: DailyReviewProgress) {
  dailyReviewProgressCache.set(userId, {
    expiresAt: Date.now() + DAILY_REVIEW_PROGRESS_TTL_MS,
    productDayKey: getReviewProductDayKey(),
    progress,
  });
}

function invalidateDailyReviewProgressCache(userId?: string) {
  if (!userId) {
    dailyReviewProgressCache.clear();
    invalidateSessionHistoryCache();
    return;
  }
  dailyReviewProgressCache.delete(userId);
  invalidateSessionHistoryCache(userId);
}

function sessionHistoryCacheKey(userId: string, limit: number, subject?: string) {
  return `${userId}::${subject || ''}::${limit}`;
}

function invalidateSessionHistoryCache(userId?: string) {
  if (!userId) {
    sessionHistoryCache.clear();
    return;
  }
  const prefix = `${userId}::`;
  for (const key of [...sessionHistoryCache.keys()]) {
    if (key.startsWith(prefix)) {
      sessionHistoryCache.delete(key);
    }
  }
}

function getCachedSessionHistory(userId: string, limit: number, subject?: string) {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) {
    return null;
  }

  const exact = sessionHistoryCache.get(sessionHistoryCacheKey(normalizedUserId, limit, subject));
  if (exact && exact.expiresAt > Date.now()) {
    return exact.history.slice(0, limit);
  }

  // Larger cached limit can satisfy a smaller request for the same user + subject filter.
  const userPrefix = `${normalizedUserId}::`;
  for (const [cacheKey, entry] of sessionHistoryCache.entries()) {
    if (!cacheKey.startsWith(userPrefix)) continue;
    if (entry.expiresAt <= Date.now()) continue;
    if ((entry.subject || '') !== (subject || '')) continue;
    if (entry.limit < limit) continue;
    return entry.history.slice(0, limit);
  }
  return null;
}

function setCachedSessionHistory(
  userId: string,
  limit: number,
  history: LearningEpisodeSessionHistoryEntry[],
  subject?: string,
) {
  sessionHistoryCache.set(sessionHistoryCacheKey(userId, limit, subject), {
    expiresAt: Date.now() + SESSION_HISTORY_TTL_MS,
    limit,
    subject,
    history,
  });
}

function readUserIdFromPlanCacheKey(cacheKey: string) {
  return String(cacheKey || '').split(':')[0] || '';
}

function mergeLeadThoughtByParticleId(
  base: Record<string, LECLeadThoughtEntry> | undefined,
  incoming: Record<string, LECLeadThoughtEntry> | undefined,
  particleId: string,
  fallbackEntry: LECLeadThoughtEntry,
) {
  const baseEntry = base?.[particleId];
  const incomingEntry = incoming?.[particleId];

  return {
    ...(base || {}),
    ...(incoming || {}),
    [particleId]: {
      ...(baseEntry || {}),
      ...(incomingEntry || {}),
      ...fallbackEntry,
    },
  };
}

function mergeFamilyFeedbackByParticleId(
  base: Record<string, {
    score?: number | null;
    feedback?: string | null;
    submittedAt: string;
    printSubQuestionsExpanded?: boolean | null;
  }> | undefined,
  incoming: Record<string, {
    score?: number | null;
    feedback?: string | null;
    submittedAt: string;
    printSubQuestionsExpanded?: boolean | null;
  }> | undefined,
  particleId: string,
  fallbackEntry: {
    score?: number | null;
    feedback?: string | null;
    submittedAt: string;
    printSubQuestionsExpanded?: boolean | null;
  },
) {
  const baseEntry = base?.[particleId];
  const incomingEntry = incoming?.[particleId];

  return {
    ...(base || {}),
    ...(incoming || {}),
    [particleId]: {
      ...(baseEntry || {}),
      ...(incomingEntry || {}),
      ...fallbackEntry,
    },
  };
}

function isSameReviewDay(input?: string | null, dayBoundaryHour: number = 3) {
  return isSameReviewProductDay(input, new Date(), dayBoundaryHour);
}

function clearAllCachedSurfacesForUser(userId: string) {
  const normalizedUserId = String(userId || '').trim();
  if (!normalizedUserId) {
    learningEpisodeSurfaceCache.clear();
    return;
  }
  for (const cacheKey of Array.from(learningEpisodeSurfaceCache.keys())) {
    if (String(cacheKey).startsWith(`${normalizedUserId}:`)) {
      learningEpisodeSurfaceCache.delete(cacheKey);
    }
  }
}

function readPersistedReviewProductDay(userId: string): string | null {
  try {
    const value = window.localStorage.getItem(`${REVIEW_PRODUCT_DAY_STORAGE_PREFIX}${userId}`);
    return String(value || '').trim() || null;
  } catch {
    return null;
  }
}

function writePersistedReviewProductDay(userId: string, dayKey: string): void {
  try {
    window.localStorage.setItem(`${REVIEW_PRODUCT_DAY_STORAGE_PREFIX}${userId}`, dayKey);
  } catch {
    // ignore
  }
}

function parseReviewParticleVector(raw: unknown): ReviewParticleVector[] {
  if (!raw) {
    return [];
  }

  let payload = raw;
  if (typeof raw === 'string') {
    try {
      payload = JSON.parse(raw);
    } catch {
      return [];
    }
  }

  if (Array.isArray(payload)) {
    return payload
      .map((item) => {
        if (typeof item === 'string' && item.trim()) {
          return { kpid: item.trim(), rsc: 1 };
        }
        if (!item || typeof item !== 'object') {
          return null;
        }
        const record = item as { kpid?: unknown; rsc?: unknown };
        const kpid = typeof record.kpid === 'string' ? record.kpid.trim() : '';
        if (!kpid) {
          return null;
        }
        return {
          kpid,
          rsc: typeof record.rsc === 'number' && Number.isFinite(record.rsc) ? record.rsc : 1,
        };
      })
      .filter((item): item is ReviewParticleVector => Boolean(item));
  }

  if (payload && typeof payload === 'object' && Array.isArray((payload as { ps?: unknown[] }).ps)) {
    return parseReviewParticleVector((payload as { ps?: unknown[] }).ps);
  }

  return [];
}

function orderParticlesByVector(particles: KnowledgeParticle[], vector: ReviewParticleVector[]) {
  const particleMap = new Map(particles.map((particle) => [particle.id, particle]));
  return vector
    .map((item) => particleMap.get(item.kpid))
    .filter((particle): particle is KnowledgeParticle => Boolean(particle));
}

function orderParticlesByIds(particles: KnowledgeParticle[], particleIds: string[]) {
  const particleMap = new Map(particles.map((particle) => [particle.id, particle]));
  return particleIds
    .map((particleId) => particleMap.get(particleId))
    .filter((particle): particle is KnowledgeParticle => Boolean(particle));
}

async function getKnowledgeParticlesByIds(particleIds: string[]) {
  if (!particleIds.length) {
    return [];
  }

  return requestMaybeEnvelope<KnowledgeParticle[]>('/api/knowledge-particles/batch', {
    method: 'POST',
    body: { particleIds },
  });
}

type PlanReadyApiResponse =
  | {
      status: 'ready';
      source: 'ready_store' | 'session';
      slotId: string;
      planId?: string | null;
      sessionId?: string | null;
      episode: LearningEpisodePayload;
      particleIds: string[];
      trace?: LearningEpisodeTraceStep[];
    }
  | {
      status: 'building';
      slotId: string;
      buildStartedAt: string;
      trace?: LearningEpisodeTraceStep[];
      buildStage?: 'materializing' | 'selecting_particles' | 'finalizing';
      estimatedReadyMs?: number;
      buildProgressPercent?: number;
    }
  | {
      status: 'unavailable';
      slotId?: string;
      unavailableCode?: string | null;
      unavailableReason?: string | null;
      trace?: LearningEpisodeTraceStep[];
    };

async function pollPlanReady(input: {
  userId: string;
  subject: string;
  stream: string;
  preferSession?: boolean;
  excludeParticleIds?: string[];
  intent?: 'swap';
  onTrace?: (trace: LearningEpisodeTraceStep[]) => void;
  onBuildingProgress?: (progress: Extract<PlanReadyApiResponse, { status: 'building' }>) => void;
}): Promise<PlanReadyApiResponse> {
  const params = new URLSearchParams({
    userId: input.userId,
    subject: input.subject,
    stream: input.stream,
  });
  if (input.preferSession === false) {
    params.set('preferSession', 'false');
  }
  if (input.intent === 'swap') {
    params.set('intent', 'swap');
  }
  if (input.excludeParticleIds?.length) {
    params.set('excludeParticleIds', input.excludeParticleIds.join(','));
  }
  const startedAt = Date.now();
  const emittedTraceKeys = new Set<string>();
  let pollDelayMs = LEARNING_EPISODE_BUILD_POLL_INTERVAL_MS;

  while (Date.now() - startedAt < LEARNING_EPISODE_BUILD_TIMEOUT_MS) {
    const ready = await requestMaybeEnvelope<PlanReadyApiResponse>(
      `/api/review/learning-episode-plans/ready?${params.toString()}`,
      { method: 'GET' },
    );
    emitNewLearningEpisodeTraceSteps(emittedTraceKeys, Array.isArray(ready.trace) ? ready.trace : undefined, input.onTrace);

    if (ready.status === 'ready' || ready.status === 'unavailable') {
      return ready;
    }

    input.onBuildingProgress?.(ready);
    await delay(pollDelayMs);
    pollDelayMs = Math.min(pollDelayMs + 500, 5_000);
  }

  throw new Error('学习序列生成超时');
}

function mapPlanReadyUnavailableState(
  unavailableCode?: string | null,
): ReviewHomeSurface['unavailableState'] {
  switch (String(unavailableCode || '').trim()) {
    case 'no_particles':
      return 'no_particles';
    case 'build_failed':
    case 'plan_ready_store_disabled':
      return 'service_unavailable';
    case 'provider_missing':
    case 'provider_disabled':
    case 'provider_stream_mismatch':
    case 'provider_subject_mismatch':
    case 'stream_placeholder':
    case 'stream_paused':
      return 'provider_not_ready';
    default:
      return 'data_not_ready';
  }
}

function buildLearningEpisodeUnavailableError(ready: Extract<PlanReadyApiResponse, { status: 'unavailable' }>) {
  const error = new Error(ready.unavailableReason || '当前没有可用的学习序列');
  (error as Error & { unavailableCode?: string | null; unavailableState?: ReviewHomeSurface['unavailableState'] }).unavailableCode = ready.unavailableCode;
  (error as Error & { unavailableCode?: string | null; unavailableState?: ReviewHomeSurface['unavailableState'] }).unavailableState = mapPlanReadyUnavailableState(ready.unavailableCode);
  return error;
}

async function mapReadyResponseToLearningEpisodePlan(input: {
  ready: Extract<PlanReadyApiResponse, { status: 'ready' }>;
  request: LearningEpisodeRequest;
  cacheKey: string;
  userId: string;
}): Promise<LearningEpisodePlan | null> {
  let episode = normalizeLearningEpisodePayload(input.ready.episode);
  let particles = Array.isArray(episode.particles) ? episode.particles : [];

  if (!particles.length && input.ready.particleIds.length > 0) {
    particles = orderParticlesByIds(
      await getKnowledgeParticlesByIds(input.ready.particleIds),
      input.ready.particleIds,
    );
    episode = {
      ...episode,
      particles,
    };
  }

  if (!particles.length) {
    return null;
  }

  const subject = input.request.subject ?? episode.subjectCode;
  const stream = input.request.stream ?? episode.streamKey;

  if (input.ready.source === 'session' && input.ready.sessionId) {
    const session = await getActiveLearningEpisodeSession(input.userId, subject, stream);
    if (session?.particleIds?.length) {
      const restoredPlan = buildLearningEpisodePlanFromSession({
        session,
        request: input.request,
        cacheKey: input.cacheKey,
        particles,
      });
      if (restoredPlan) {
        return {
          ...restoredPlan,
          planId: null,
        };
      }
    }
  }

  return {
    kind: 'learning_episode',
    label: '学习序列',
    cacheKey: input.cacheKey,
    request: input.request,
    episode,
    particles,
    currentIndex: 0,
    pendingCount: particles.length,
    totalCount: Math.max(episode.selectedFamilyCount || 0, particles.length),
    source: input.ready.source === 'session' ? 'restored' : 'generated',
    planId: input.ready.source === 'ready_store' ? (input.ready.planId ?? null) : null,
    sessionId: input.ready.sessionId ?? null,
    leadThoughtByParticleId: {},
    familyFeedbackByParticleId: {},
  };
}

async function loadLearningEpisodePlanViaReadyStore(input: LoadReviewSequenceInput): Promise<LearningEpisodePlan | null> {
  const request = buildLearningEpisodeRequest({
    algorithm: input.learningEpisode?.algorithm,
    subject: input.learningEpisode?.subject ?? input.subjectFilter,
    stream: input.learningEpisode?.stream ?? input.stream,
    size: input.learningEpisode?.size ?? input.sequenceLengthOverride,
    recentAssetLimit: input.learningEpisode?.recentAssetLimit,
  });
  const cacheKey = buildLearningEpisodeCacheKey({
    userId: input.userId,
    request,
  });
  const subject = request.subject;
  const stream = request.stream;

  if (!subject || !stream) {
    throw new Error('学习序列缺少 subject 或 stream');
  }

  const ready = await pollPlanReady({
    userId: input.userId,
    subject,
    stream,
    preferSession: input.preferSession,
    onTrace: input.onLearningEpisodeTrace,
    onBuildingProgress: input.onBuildingProgress,
  });

  if (ready.status === 'unavailable') {
    throw buildLearningEpisodeUnavailableError(ready);
  }

  if (ready.status !== 'ready') {
    throw new Error('学习序列生成超时');
  }

  return mapReadyResponseToLearningEpisodePlan({
    ready,
    request,
    cacheKey,
    userId: input.userId,
  });
}

async function getLearningEpisode(input: {
  userId: string;
  request: LearningEpisodeRequest;
  refreshHints?: LearningEpisodeRefreshHints;
  onTrace?: (trace: LearningEpisodeTraceStep[]) => void;
}) {
  const request = buildLearningEpisodeRequest(input.request);
  const excludeParticleIds = Array.isArray(input.refreshHints?.excludeParticleIds)
    ? input.refreshHints.excludeParticleIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];
  const excludeNodeIds = Array.isArray(input.refreshHints?.excludeNodeIds)
    ? input.refreshHints.excludeNodeIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];

  const build = await requestMaybeEnvelope<LearningEpisodeBuildSnapshot>('/api/review/learning-episodes/builds', {
    method: 'POST',
    body: {
      userId: input.userId,
      stream: request.stream,
      subject: request.subject,
      size: request.size,
      recentAssetLimit: request.recentAssetLimit,
      excludeParticleIds: excludeParticleIds.length > 0 ? excludeParticleIds : undefined,
      excludeNodeIds: excludeNodeIds.length > 0 ? excludeNodeIds : undefined,
    },
  });

  if (import.meta.env.DEV) {
    console.debug('[reviewService] learning-episode build started', {
      buildId: build.buildId,
      stream: request.stream,
      subject: request.subject,
      size: request.size,
      excludeParticleCount: excludeParticleIds.length,
      excludeNodeCount: excludeNodeIds.length,
    });
  }

  const emittedTraceKeys = new Set<string>();
  emitNewLearningEpisodeTraceSteps(emittedTraceKeys, Array.isArray(build.trace) ? build.trace : undefined, input.onTrace);

  let snapshot = build;
  const startedAt = Date.now();

  while (snapshot.status === 'queued' || snapshot.status === 'running') {
    if (Date.now() - startedAt >= LEARNING_EPISODE_BUILD_TIMEOUT_MS) {
      throw new Error('学习序列生成超时，请稍后重试');
    }

    await delay(LEARNING_EPISODE_BUILD_POLL_INTERVAL_MS);
    snapshot = await requestMaybeEnvelope<LearningEpisodeBuildSnapshot>(
      `/api/review/learning-episodes/builds/${encodeURIComponent(snapshot.buildId)}`,
      { method: 'GET' },
    );
    emitNewLearningEpisodeTraceSteps(emittedTraceKeys, Array.isArray(snapshot.trace) ? snapshot.trace : undefined, input.onTrace);
  }

  if (snapshot.status === 'failed') {
    throw new Error(snapshot.error || '学习序列生成失败');
  }

  if (!snapshot.episode) {
    throw new Error('学习序列生成已完成，但未返回有效结果');
  }

  return snapshot.episode;
}

async function getActiveLearningEpisodeSession(userId: string, subject?: string, stream?: string) {
  const params = new URLSearchParams({ userId });
  if (subject) params.set('subject', subject);
  if (stream) params.set('stream', stream);
  return requestMaybeEnvelope<LECFeedbackSession | null>(
    `/api/review/learning-episodes/sessions/active?${params.toString()}`,
    { method: 'GET' },
  );
}

function scoreToReviewFeedback(score: number | null | undefined): ReviewFeedbackKind | null {
  if (typeof score !== 'number' || !Number.isFinite(score)) {
    return null;
  }
  if (score >= 85) {
    return 'proficient';
  }
  if (score >= 60) {
    return 'fuzzy';
  }
  return 'forgotten';
}

function normalizeSessionHistoryEntry(entry: LearningEpisodeSessionHistoryEntry): LearningEpisodeSessionHistoryEntry {
  const reviewedFamilies = Array.isArray(entry.reviewedFamilies) ? entry.reviewedFamilies : [];
  return {
    ...entry,
    status: entry.status === 'in_progress' ? 'in_progress' : 'completed',
    completedAt: entry.completedAt ?? null,
    reviewedFamilies,
    proficientCount: entry.proficientCount ?? 0,
    fuzzyCount: entry.fuzzyCount ?? 0,
    forgottenCount: entry.forgottenCount ?? 0,
  };
}

function buildReviewedFamiliesFromSession(
  session: LECFeedbackSession,
  titleByParticleId: Map<string, string>,
): LearningEpisodeSessionHistoryEntry['reviewedFamilies'] {
  const reviewedById = new Map(session.reviewedParticles.map((entry) => [entry.particleId, entry]));
  const candidateIds = Array.from(new Set([
    ...session.particleIds,
    ...session.reviewedParticles.map((entry) => entry.particleId),
    ...Object.keys(session.familyFeedbackByParticleId || {}),
  ].map((id) => String(id || '').trim()).filter(Boolean)));

  const families = candidateIds.flatMap((particleId) => {
    const reviewed = reviewedById.get(particleId);
    const familyEntry = session.familyFeedbackByParticleId?.[particleId];
    const feedback = reviewed?.feedback || scoreToReviewFeedback(familyEntry?.score) || null;
    const reviewedAt = reviewed?.reviewedAt || familyEntry?.submittedAt || null;
    const score = typeof familyEntry?.score === 'number' && Number.isFinite(familyEntry.score)
      ? familyEntry.score
      : null;

    if (!feedback && !reviewedAt && score == null) {
      return [];
    }

    return [{
      particleId,
      particleTitle: titleByParticleId.get(particleId) || null,
      feedback,
      reviewedAt,
      score,
    }];
  });

  return families.sort((left, right) => {
    const leftTime = left.reviewedAt ? new Date(left.reviewedAt).getTime() : 0;
    const rightTime = right.reviewedAt ? new Date(right.reviewedAt).getTime() : 0;
    return rightTime - leftTime;
  });
}

function countFeedbackTotalsFromFamilies(
  families: LearningEpisodeSessionHistoryEntry['reviewedFamilies'],
): { proficientCount: number; fuzzyCount: number; forgottenCount: number } {
  let proficientCount = 0;
  let fuzzyCount = 0;
  let forgottenCount = 0;

  for (const family of families) {
    if (family.feedback === 'proficient') proficientCount++;
    else if (family.feedback === 'fuzzy') fuzzyCount++;
    else if (family.feedback === 'forgotten') forgottenCount++;
  }

  return { proficientCount, fuzzyCount, forgottenCount };
}

async function resolveParticleTitlesForHistory(particleIds: string[]): Promise<Map<string, string>> {
  const uniqueIds = Array.from(new Set(particleIds.map((id) => String(id || '').trim()).filter(Boolean)));
  if (!uniqueIds.length) {
    return new Map();
  }

  const titleById = new Map<string, string>();
  await Promise.all(uniqueIds.slice(0, 48).map(async (particleId) => {
    try {
      const particle = await knowledgeService.getKnowledgeParticle(particleId);
      const title = String(particle.title || '').trim();
      if (title) {
        titleById.set(particleId, title);
      }
    } catch {
      // Title lookup is best-effort for footprint display.
    }
  }));

  return titleById;
}

function sessionToHistoryEntry(
  session: LECFeedbackSession,
  titleByParticleId: Map<string, string>,
): LearningEpisodeSessionHistoryEntry {
  const reviewedFamilies = buildReviewedFamiliesFromSession(session, titleByParticleId);
  const totals = countFeedbackTotalsFromFamilies(reviewedFamilies);

  return normalizeSessionHistoryEntry({
    sessionId: session.id,
    subject: String(session.subject || 'math'),
    stream: String(session.stream || 'flashcard_drill'),
    algorithm: session.algorithm ? String(session.algorithm) : null,
    status: session.status === 'completed' ? 'completed' : 'in_progress',
    particleCount: session.particleIds.length,
    startedAt: session.startedAt,
    completedAt: session.completedAt ?? null,
    proficientCount: totals.proficientCount,
    fuzzyCount: totals.fuzzyCount,
    forgottenCount: totals.forgottenCount,
    completionScore: session.completionScore ?? null,
    completionFeedback: session.completionFeedback ?? null,
    reviewedFamilies,
  });
}

async function loadActiveSessionHistoryEntries(
  userId: string,
  subjectFilter?: string,
): Promise<LearningEpisodeSessionHistoryEntry[]> {
  const subjects: SubjectCode[] = subjectFilter
    ? [normalizeSubjectCode(subjectFilter)]
    : CORE_SUBJECT_CODES;

  const activeSessions = (
    await Promise.all(
      subjects.flatMap((subject) => getAvailableStreamsForSubject(subject).map(async (stream) => {
        try {
          const session = await getActiveLearningEpisodeSession(userId, subject, stream);
          if (!session || session.status !== 'started') {
            return null;
          }

          const hasReviewedFamilies = session.reviewedParticles.length > 0
            || Object.keys(session.familyFeedbackByParticleId || {}).length > 0;
          if (!hasReviewedFamilies) {
            return null;
          }

          return session;
        } catch {
          return null;
        }
      })),
    )
  ).filter((session): session is LECFeedbackSession => Boolean(session));

  if (!activeSessions.length) {
    return [];
  }

  const particleIds = activeSessions.flatMap((session) => [
    ...session.particleIds,
    ...session.reviewedParticles.map((entry) => entry.particleId),
    ...Object.keys(session.familyFeedbackByParticleId || {}),
  ]);
  const titleByParticleId = await resolveParticleTitlesForHistory(particleIds);

  return activeSessions.map((session) => sessionToHistoryEntry(session, titleByParticleId));
}

async function startLearningEpisodeSession(
  plan: LearningEpisodePlan,
  options?: { forceReplace?: boolean },
) {
  const subject = plan.request.subject ?? plan.episode.subjectCode;
  const stream = plan.request.stream;

  if (!subject) {
    throw new Error('学习序列缺少subject，无法启动LEC会话');
  }

  if (!stream) {
    throw new Error('学习序列缺少stream，无法启动LEC会话');
  }

  return requestMaybeEnvelope<LECFeedbackSession>('/api/review/learning-episodes/sessions/start', {
    method: 'POST',
    body: {
      sourceEpisodeId: plan.episode.id,
      planId: plan.source === 'generated' && plan.planId ? plan.planId : undefined,
      subject,
      stream,
      algorithm: plan.request.algorithm ?? plan.episode.algorithm,
      particleIds: plan.particles.map((particle) => particle.id),
      openingParticleId: streamUsesOpeningCard(stream as LearningEpisodeStream) ? (plan.particles[0]?.id || null) : null,
      forceReplace: options?.forceReplace === true,
    },
  });
}

async function updateLearningEpisodeSessionProgress(input: {
  sessionId?: string | null;
  particleIds?: string[];
  reviewedParticleId?: string;
  reviewedAt?: string;
  feedback?: ReviewFeedbackKind;
  leadThought?: {
    particleId: string;
    text?: string | null;
    updatedAt?: string | null;
    inputMode?: LECLeadThoughtInputMode | null;
  };
  familyFeedback?: {
    particleId: string;
    score?: number | null;
    feedback?: string | null;
    submittedAt?: string | null;
    printSubQuestionsExpanded?: boolean | null;
  };
  subCardFeedback?: {
    particleId: string;
    cardFeedbacks: Array<{ cardId: string; rating: ReviewFeedbackKind }>;
    submittedAt?: string | null;
    clientRequestId?: string | null;
  };
  familyPresentation?: {
    particleId: string;
    cardId?: string | null;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
    familyIndex?: number | null;
    familyCardIndex?: number | null;
    occurredAt?: string | null;
    clientRequestId?: string | null;
  };
  familyAction?: {
    particleId: string;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
    actionIntent: string;
    intentClass: ReviewLongPressIntentClass;
    qualityReasonCodes?: string[] | null;
    qualityReasonLabel?: string | null;
    qualityReasonDescription?: string | null;
    noteText?: string | null;
    occurredAt?: string | null;
    clientRequestId?: string | null;
  };
  familyCompletion?: {
    particleId: string;
    completedAt?: string | null;
    feedback?: ReviewFeedbackKind | null;
    summaryScore?: number | null;
    summaryText?: string | null;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
    clientRequestId?: string | null;
  };
  deckCursor?: {
    particleId: string;
    cardId?: string | null;
    familyCardIndex?: number | null;
    updatedAt?: string | null;
  };
}) {
  return requestMaybeEnvelope<LECFeedbackSession>('/api/review/learning-episodes/sessions/progress', {
    method: 'POST',
    body: {
      sessionId: input.sessionId,
      particleIds: input.particleIds,
      reviewedParticleId: input.reviewedParticleId,
      reviewedAt: input.reviewedAt,
      feedback: input.feedback,
      leadThought: input.leadThought ? {
        particleId: input.leadThought.particleId,
        text: input.leadThought.text,
        updatedAt: input.leadThought.updatedAt,
        inputMode: input.leadThought.inputMode,
      } : undefined,
      familyFeedback: input.familyFeedback ? {
        particleId: input.familyFeedback.particleId,
        score: input.familyFeedback.score,
        feedback: input.familyFeedback.feedback,
        submittedAt: input.familyFeedback.submittedAt,
        printSubQuestionsExpanded: input.familyFeedback.printSubQuestionsExpanded,
      } : undefined,
      subCardFeedback: input.subCardFeedback ? {
        particleId: input.subCardFeedback.particleId,
        cardFeedbacks: input.subCardFeedback.cardFeedbacks,
        submittedAt: input.subCardFeedback.submittedAt,
        clientRequestId: input.subCardFeedback.clientRequestId,
      } : undefined,
      familyPresentation: input.familyPresentation ? {
        particleId: input.familyPresentation.particleId,
        cardId: input.familyPresentation.cardId,
        cardKind: input.familyPresentation.cardKind,
        streamDecorationKind: input.familyPresentation.streamDecorationKind,
        familyIndex: input.familyPresentation.familyIndex,
        familyCardIndex: input.familyPresentation.familyCardIndex,
        occurredAt: input.familyPresentation.occurredAt,
        clientRequestId: input.familyPresentation.clientRequestId,
      } : undefined,
      familyAction: input.familyAction ? {
        particleId: input.familyAction.particleId,
        cardKind: input.familyAction.cardKind,
        streamDecorationKind: input.familyAction.streamDecorationKind,
        actionIntent: input.familyAction.actionIntent,
        intentClass: input.familyAction.intentClass,
        qualityReasonCodes: input.familyAction.qualityReasonCodes,
        qualityReasonLabel: input.familyAction.qualityReasonLabel,
        qualityReasonDescription: input.familyAction.qualityReasonDescription,
        noteText: input.familyAction.noteText,
        occurredAt: input.familyAction.occurredAt,
        clientRequestId: input.familyAction.clientRequestId,
      } : undefined,
      familyCompletion: input.familyCompletion ? {
        particleId: input.familyCompletion.particleId,
        completedAt: input.familyCompletion.completedAt,
        feedback: input.familyCompletion.feedback,
        summaryScore: input.familyCompletion.summaryScore,
        summaryText: input.familyCompletion.summaryText,
        cardKind: input.familyCompletion.cardKind,
        streamDecorationKind: input.familyCompletion.streamDecorationKind,
        clientRequestId: input.familyCompletion.clientRequestId,
      } : undefined,
      deckCursor: input.deckCursor ? {
        particleId: input.deckCursor.particleId,
        cardId: input.deckCursor.cardId,
        familyCardIndex: input.deckCursor.familyCardIndex,
        updatedAt: input.deckCursor.updatedAt,
      } : undefined,
    },
  });
}

async function completeLearningEpisodeSession(input: {
  sessionId?: string | null;
  completionScore?: number | null;
  completionFeedback?: string | null;
  completionSummary?: string | null;
}) {
  return requestMaybeEnvelope<LECFeedbackSession>('/api/review/learning-episodes/sessions/complete', {
    method: 'POST',
    body: {
      sessionId: input.sessionId,
      completionScore: input.completionScore,
      completionFeedback: input.completionFeedback,
      completionSummary: input.completionSummary,
    },
  });
}

type DeckCursorCardRef = {
  id: string;
  particleId: string;
  familyCardIndex?: number;
};

export function buildLECDeckCursorFromCard(card: DeckCursorCardRef | null | undefined): LECDeckCursor | null {
  if (!card?.particleId || !card.id) {
    return null;
  }

  return {
    particleId: card.particleId,
    cardId: card.id,
    familyCardIndex: typeof card.familyCardIndex === 'number' ? card.familyCardIndex : null,
    updatedAt: new Date().toISOString(),
  };
}

export function resolveDeckCursorIndex(
  cards: DeckCursorCardRef[],
  cursor: LECDeckCursor | null | undefined,
): number {
  if (!cursor || !cards.length) {
    return 0;
  }

  if (cursor.cardId) {
    const byCardId = cards.findIndex((card) => card.id === cursor.cardId);
    if (byCardId >= 0) {
      return byCardId;
    }
  }

  if (cursor.particleId && typeof cursor.familyCardIndex === 'number') {
    const byFamilyCardIndex = cards.findIndex(
      (card) => card.particleId === cursor.particleId && card.familyCardIndex === cursor.familyCardIndex,
    );
    if (byFamilyCardIndex >= 0) {
      return byFamilyCardIndex;
    }
  }

  if (cursor.particleId) {
    const byParticleId = cards.findIndex((card) => card.particleId === cursor.particleId);
    if (byParticleId >= 0) {
      return byParticleId;
    }
  }

  return 0;
}

function buildLearningEpisodePlanFromSession(params: {
  session: LECFeedbackSession;
  request: LearningEpisodeRequest;
  cacheKey: string;
  particles: KnowledgeParticle[];
}): LearningEpisodePlan | null {
  const restoredFamilyFeedbackForStaleCheck = params.session.familyFeedbackByParticleId || {};
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const staleParticleIds = new Set(
    Object.entries(restoredFamilyFeedbackForStaleCheck)
      .filter(([, entry]) => {
        if (!entry.submittedAt) return false;
        const hasScoreOrFeedback = typeof entry.score === 'number' || Boolean(entry.feedback?.trim());
        const isStale = new Date(entry.submittedAt).getTime() < todayStart.getTime();
        return hasScoreOrFeedback && isStale;
      })
      .map(([id]) => id),
  );

  const orderedParticles = orderParticlesByIds(params.particles, params.session.particleIds)
    .filter((p) => !staleParticleIds.has(p.id));

  if (!orderedParticles.length) {
    return null;
  }

  const algorithm = normalizeLearningEpisodeAlgorithm(params.session.algorithm || params.request.algorithm);
  const subject = normalizeEpisodeSubject(params.session.subject || params.request.subject);
  const totalCount = Math.max(
    orderedParticles.length + params.session.reviewedParticles.length,
    orderedParticles.length,
  );
  const generatedAt = params.session.startedAt || new Date().toISOString();
  const rawEpisode: LearningEpisodePayload = {
    id: params.session.sourceEpisodeId || `lec-feedback-session:${params.session.id}`,
    title: '已恢复学习序列',
    algorithm,
    algorithmLabel: `${buildLearningEpisodeAlgorithmLabel(algorithm)} (restored)`,
    subjectCode: subject,
    subjectLabel: buildEpisodeSubjectLabel(subject),
    status: 'ready',
    targetFamilyCount: totalCount,
    selectedFamilyCount: orderedParticles.length,
    recentAssetCount: 0,
    recentParticleCount: orderedParticles.length,
    dominantProfileId: undefined,
    dominantProfileName: undefined,
    dominantSpineId: undefined,
    dominantSpineTitle: undefined,
    anchorNode: null,
    candidateNodeTitles: orderedParticles.map((particle, index) => buildParticlePreviewTitle(particle, index)),
    summary: '已恢复上次未完成的学习序列，会从剩余卡族继续。',
    buildTrace: [
      {
        key: 'lec-feedback-session-restored',
        label: '恢复已开始会话',
        detail: `已恢复 ${orderedParticles.length} 个剩余知识颗粒。`,
        tone: 'info',
      },
    ],
    families: orderedParticles.map((particle, index) => {
      const binding = getSubjectValueBindingRecord(particle.metadata);
      const particleTitle = buildParticlePreviewTitle(particle, index);
      return {
        particleId: particle.id,
        particleTitle,
        nodeId: readBindingText(binding, ['spineNodeId']) || particle.id,
        nodeTitle: readBindingText(binding, ['spineNodeTitle', 'candidateSpineNodeTitle']) || particleTitle,
        nodeNo: readBindingNumber(binding, ['nodeNo', 'candidateNodeNo']) || index + 1,
        moduleKey: readBindingText(binding, ['moduleKey']),
      };
    }),
    particles: orderedParticles,
    generatedAt,
  };
  const episode = normalizeLearningEpisodePayload(rawEpisode);

  // Exclude particles already submitted (familyFeedbackByParticleId.submittedAt set) from the
  // active deck so the badge count and initial index reflect truly unreviewed families only.
  // Submitted-but-not-swiped particles remain in episode.families for visual display in the
  // sequence sheet (they appear as "已刷" via submittedParticleIds).
  // We use score presence (not submittedAt) as the "reviewed" indicator because print-toggle
  // saves also set submittedAt but carry no score and must not count as reviewed.
  const restoredFamilyFeedback = params.session.familyFeedbackByParticleId || {};
  const submittedParticleIds = new Set(
    Object.entries(restoredFamilyFeedback)
      .filter(([, entry]) => typeof entry.score === 'number' || Boolean(entry.feedback?.trim()))
      .map(([id]) => id),
  );
  const pendingParticles = submittedParticleIds.size > 0
    ? orderedParticles.filter((p) => !submittedParticleIds.has(p.id))
    : orderedParticles;

  return {
    kind: 'learning_episode',
    label: '学习序列',
    cacheKey: params.cacheKey,
    request: buildLearningEpisodeRequest({
      algorithm,
      subject,
      stream: params.session.stream || params.request.stream,
      size: orderedParticles.length,
      recentAssetLimit: params.request.recentAssetLimit,
    }),
    episode,
    particles: pendingParticles,
    currentIndex: 0,
    pendingCount: pendingParticles.length,
    totalCount,
    source: 'restored',
    sessionId: params.session.id,
    leadThoughtByParticleId: params.session.leadThoughtByParticleId || {},
    familyFeedbackByParticleId: restoredFamilyFeedback,
    deckCursor: params.session.deckCursor ?? null,
  };
}

async function loadLearningEpisodePlan(input: LoadReviewSequenceInput): Promise<LearningEpisodePlan | null> {
  if (USE_PLAN_READY_STORE) {
    const request = buildLearningEpisodeRequest({
      algorithm: input.learningEpisode?.algorithm,
      subject: input.learningEpisode?.subject ?? input.subjectFilter,
      stream: input.learningEpisode?.stream ?? input.stream,
      size: input.learningEpisode?.size ?? input.sequenceLengthOverride,
      recentAssetLimit: input.learningEpisode?.recentAssetLimit,
    });
    const excludeParticleIds = Array.isArray(input.learningEpisodeRefreshHints?.excludeParticleIds)
      ? input.learningEpisodeRefreshHints.excludeParticleIds.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
      : [];

    if (input.forceRefresh && excludeParticleIds.length > 0 && request.subject && request.stream) {
      await requestMaybeEnvelope<PlanReadyApiResponse>('/api/review/learning-episode-plans/ensure', {
        method: 'POST',
        body: {
          userId: input.userId,
          subject: request.subject,
          stream: request.stream,
          intent: 'swap',
          excludeParticleIds,
        },
      });
    }

    return loadLearningEpisodePlanViaReadyStore(input);
  }

  const request = buildLearningEpisodeRequest({
    algorithm: input.learningEpisode?.algorithm,
    subject: input.learningEpisode?.subject ?? input.subjectFilter,
    stream: input.learningEpisode?.stream ?? input.stream,
    size: input.learningEpisode?.size ?? input.sequenceLengthOverride,
    recentAssetLimit: input.learningEpisode?.recentAssetLimit,
  });
  const cacheKey = buildLearningEpisodeCacheKey({
    userId: input.userId,
    request,
  });

  if (!input.forceRefresh) {
    const activeSession = await getActiveLearningEpisodeSession(
      input.userId,
      request.subject,
      request.stream,
    );
    if (activeSession?.particleIds?.length) {
      const restoredParticles = await getKnowledgeParticlesByIds(activeSession.particleIds);
      const restoredPlan = buildLearningEpisodePlanFromSession({
        session: activeSession,
        request,
        cacheKey,
        particles: restoredParticles,
      });
      if (restoredPlan) {
        return restoredPlan;
      }
    }
  }

  const episode = normalizeLearningEpisodePayload(await getLearningEpisode({
    userId: input.userId,
    request,
    refreshHints: input.learningEpisodeRefreshHints,
    onTrace: input.onLearningEpisodeTrace,
  }));

  if (!episode || !Array.isArray(episode.particles)) {
    return null;
  }

  return {
    kind: 'learning_episode',
    label: '学习序列',
    cacheKey,
    request,
    episode,
    particles: episode.particles,
    currentIndex: 0,
    pendingCount: episode.particles.length,
    totalCount: Math.max(episode.selectedFamilyCount || 0, episode.particles.length),
    source: 'generated',
    sessionId: null,
    leadThoughtByParticleId: {},
    familyFeedbackByParticleId: {},
  };
}

export const reviewService = {
  getLearningSequenceSettings,
  updateLearningSequenceSettings,
  resolveSequenceLengthForSubjectStream,
  buildConfiguredLearningEpisodeRequest,
  syncLearningEpisodeStreamRegistry,

  peekDailyReviewProgress(userId: string): DailyReviewProgress | null {
    return getCachedDailyReviewProgress(userId);
  },

  async loadDailyReviewProgress(
    userId: string,
    options?: { forceRefresh?: boolean; peekMissingSurfaces?: boolean },
  ): Promise<DailyReviewProgress> {
    const previousCached = getCachedDailyReviewProgress(userId);
    // Sticky cache wins: peekMissingSurfaces must not re-fan-out when already cached.
    if (!options?.forceRefresh && previousCached) {
      return previousCached;
    }

    await syncLearningEpisodeStreamRegistry({ forceRefresh: options?.forceRefresh });

    let progress = await collectDailyReviewProgressFromSessionsAndCache(userId);

    if (options?.peekMissingSurfaces) {
      const settings = readLearningSequenceSettings(userId);
      const peekEntries = [...progress.entries];
      await Promise.all(
        CORE_SUBJECT_CODES.flatMap((subject) => getAvailableStreamsForSubject(subject).map(async (stream) => {
          if (isShuffleInfiniteStream(stream)) {
            return;
          }

          const configuredLength = resolveSequenceLengthForSubjectStream(settings, subject, stream);
          const existing = peekEntries.find((entry) => normalizeSubjectCode(entry.subject) === subject && entry.stream === stream);
          if ((existing?.pendingCount || 0) > 0) {
            return;
          }

          let session: Pick<LECFeedbackSession, 'familyFeedbackByParticleId' | 'reviewedParticles' | 'particleIds'> | null = null;
          try {
            session = await getActiveLearningEpisodeSession(userId, subject, stream);
          } catch {
            // Best effort per stream.
          }

          const dailyPending = computeDailyReviewPendingForStream(configuredLength, session, stream);
          if (dailyPending.pendingCount <= 0) {
            return;
          }

          const nextEntry: DailyReviewProgressEntry = {
            subject,
            stream,
            pendingCount: dailyPending.pendingCount,
            targetFamilyCount: dailyPending.targetFamilyCount,
            reviewedCount: dailyPending.reviewedCount,
            isComplete: dailyPending.isComplete,
            isInfinite: false,
          };
          const index = peekEntries.findIndex((entry) => normalizeSubjectCode(entry.subject) === subject && entry.stream === stream);
          if (index >= 0) {
            peekEntries[index] = nextEntry;
          } else {
            peekEntries.push(nextEntry);
          }
        })),
      );
      progress = mergeDailyReviewProgressEntries(peekEntries);
    }

    try {
      const params = new URLSearchParams({ userId });
      const remote = await requestMaybeEnvelope<DailyReviewProgress>(
        `/api/review/learning-episodes/daily-progress?${params.toString()}`,
        { method: 'GET' },
      );
      const nextProgress: DailyReviewProgress = {
        totalPendingCount: remote.totalPendingCount ?? 0,
        bySubject: Object.fromEntries(
          Object.entries(remote.bySubject || {}).map(([subject, count]) => [normalizeSubjectCode(subject), count]),
        ),
        byStream: Object.fromEntries(
          Object.entries(remote.byStream || {}).map(([subject, streamMap]) => [
            normalizeSubjectCode(subject),
            streamMap || {},
          ]),
        ),
        entries: Array.isArray(remote.entries) ? remote.entries.map((entry) => {
          const targetFamilyCount = Math.max(0, entry.targetFamilyCount ?? 0);
          const reviewedCount = Math.max(0, entry.reviewedCount ?? 0);
          const isInfinite = isShuffleInfiniteStream(entry.stream);
          const isComplete = entry.isComplete ?? (reviewedCount >= targetFamilyCount && targetFamilyCount > 0);
          return {
            ...entry,
            subject: normalizeSubjectCode(entry.subject),
            pendingCount: isComplete
              ? 0
              : Math.max(0, entry.pendingCount ?? Math.max(0, targetFamilyCount - reviewedCount)),
            targetFamilyCount,
            reviewedCount,
            isComplete,
            isInfinite,
          };
        }) : [],
      };
      setCachedDailyReviewProgress(userId, nextProgress);
      return nextProgress;
    } catch {
      setCachedDailyReviewProgress(userId, progress);
      return progress;
    }
  },

  async ensureStreamLearningEpisodePlan(input: {
    userId: string;
    subject: string;
    stream: string;
  }): Promise<void> {
    if (!USE_PLAN_READY_STORE) {
      return;
    }

    await requestMaybeEnvelope<PlanReadyApiResponse>('/api/review/learning-episode-plans/ensure', {
      method: 'POST',
      body: {
        userId: input.userId,
        subject: input.subject,
        stream: input.stream,
        intent: 'default',
      },
    });
  },

  prewarmStreamLearningEpisodePlan(input: {
    userId: string;
    subject: string;
    stream: string;
  }): void {
    if (!USE_PLAN_READY_STORE) {
      return;
    }
    const subject = normalizeSubjectCode(input.subject);
    const stream = String(input.stream || '').trim();
    const prewarmKey = `${input.userId}:${subject}:${stream}`;
    const now = Date.now();
    const lastAt = streamPrewarmAtByKey.get(prewarmKey) || 0;
    if (now - lastAt < STREAM_PREWARM_COOLDOWN_MS) {
      return;
    }
    streamPrewarmAtByKey.set(prewarmKey, now);
    void requestMaybeEnvelope<PlanReadyApiResponse>('/api/review/learning-episode-plans/ensure', {
      method: 'POST',
      body: {
        userId: input.userId,
        subject,
        stream,
        intent: 'prewarm',
      },
    }).catch(() => undefined);
  },

  prewarmSiblingStreamPlans(input: {
    userId: string;
    subject: string;
    activeStream: string;
  }): void {
    if (!USE_PLAN_READY_STORE) {
      return;
    }
    const subject = normalizeSubjectCode(input.subject);
    const activeStream = String(input.activeStream || '').trim();
    const siblingKey = `${input.userId}:${subject}`;
    const now = Date.now();
    const lastAt = siblingPrewarmAtByKey.get(siblingKey) || 0;
    if (now - lastAt < SIBLING_PREWARM_COOLDOWN_MS) {
      return;
    }
    siblingPrewarmAtByKey.set(siblingKey, now);
    for (const stream of getAvailableStreamsForSubject(subject)) {
      if (stream === activeStream || isShuffleInfiniteStream(stream)) continue;
      void requestMaybeEnvelope<PlanReadyApiResponse>('/api/review/learning-episode-plans/ensure', {
        method: 'POST',
        body: {
          userId: input.userId,
          subject,
          stream,
          intent: 'prewarm',
        },
      }).catch(() => undefined);
    }
  },

  /**
   * 跨 product day（默认本地 03:00）时：清日进度 / sticky surface，并预热下一批。
   */
  async handleReviewProductDayRollover(input: {
    userId: string;
    subject?: string;
    activeStream?: string;
  }): Promise<{ rolled: boolean; dayKey: string }> {
    const userId = String(input.userId || '').trim();
    const dayKey = getReviewProductDayKey();
    if (!userId) {
      return { rolled: false, dayKey };
    }

    const previous = lastReviewProductDayByUserId.get(userId)
      ?? readPersistedReviewProductDay(userId);
    lastReviewProductDayByUserId.set(userId, dayKey);
    writePersistedReviewProductDay(userId, dayKey);

    if (!previous || previous === dayKey) {
      return { rolled: false, dayKey };
    }

    invalidateDailyReviewProgressCache(userId);
    clearAllCachedSurfacesForUser(userId);

    const subject = input.subject ? normalizeSubjectCode(input.subject) : null;
    const activeStream = String(input.activeStream || '').trim()
      || (subject ? getDefaultStreamForSubject(subject) : '');
    if (subject && activeStream) {
      reviewService.prewarmStreamLearningEpisodePlan({
        userId,
        subject,
        stream: activeStream,
      });
      reviewService.prewarmSiblingStreamPlans({
        userId,
        subject,
        activeStream,
      });
    }

    return { rolled: true, dayKey };
  },

  peekHomeReviewSurface(input: Pick<LoadReviewSequenceInput, 'userId' | 'subjectFilter' | 'stream' | 'learningEpisode' | 'sequenceLengthOverride'>): ReviewHomeSurface | null {
    const settings = readLearningSequenceSettings(input.userId);
    const subject = normalizeSubjectCode(input.learningEpisode?.subject ?? input.subjectFilter);
    const stream = String(input.learningEpisode?.stream ?? input.stream ?? '').trim();
    const activeSequenceLength = stream
      ? resolveSequenceLengthForSubjectStream(settings, subject, stream)
      : resolveSequenceLengthForMode(settings);
    const learningEpisodeRequest = buildLearningEpisodeRequest({
      algorithm: input.learningEpisode?.algorithm,
      subject: input.learningEpisode?.subject ?? input.subjectFilter,
      stream: input.learningEpisode?.stream ?? input.stream,
      size: input.learningEpisode?.size ?? input.sequenceLengthOverride ?? activeSequenceLength,
      recentAssetLimit: input.learningEpisode?.recentAssetLimit,
    });
    const cacheKey = buildLearningEpisodeCacheKey({
      userId: input.userId,
      request: learningEpisodeRequest,
    });
    return getCachedLearningEpisodeSurface(cacheKey);
  },

  async loadHomeReviewSurface(input: LoadReviewSequenceInput): Promise<ReviewHomeSurface> {
    const settings = readLearningSequenceSettings(input.userId);
    const subject = normalizeSubjectCode(input.learningEpisode?.subject ?? input.subjectFilter);
    const stream = String(input.learningEpisode?.stream ?? input.stream ?? '').trim();
    const activeSequenceLength = stream
      ? resolveSequenceLengthForSubjectStream(settings, subject, stream)
      : resolveSequenceLengthForMode(settings);
    const learningEpisodeRequest = buildLearningEpisodeRequest({
      algorithm: input.learningEpisode?.algorithm,
      subject: input.learningEpisode?.subject ?? input.subjectFilter,
      stream: input.learningEpisode?.stream ?? input.stream,
      size: input.learningEpisode?.size ?? input.sequenceLengthOverride ?? activeSequenceLength,
      recentAssetLimit: input.learningEpisode?.recentAssetLimit,
    });
    const cacheKey = buildLearningEpisodeCacheKey({
      userId: input.userId,
      request: learningEpisodeRequest,
    });

    if (input.forceRefresh) {
      clearCachedLearningEpisodeSurface(cacheKey);
    } else {
      const cached = getCachedLearningEpisodeSurface(cacheKey);
      if (cached) {
        return cached;
      }
    }

    await syncLearningEpisodeStreamRegistry({ forceRefresh: false });

    if (!input.forceRefresh) {
      const cachedAfterRegistry = getCachedLearningEpisodeSurface(cacheKey);
      if (cachedAfterRegistry) {
        return cachedAfterRegistry;
      }
    }

    let sequencePlan: LearningEpisodePlan | null = null;
    let unavailableReason = '';
    let unavailableState: ReviewHomeSurface['unavailableState'];
    let unavailableCode: ReviewHomeSurface['unavailableCode'];
    let didSequencePlanLoadFail = false;

    try {
      const learningEpisodePlan = await loadLearningEpisodePlan({
        ...input,
        subjectFilter: learningEpisodeRequest.subject,
        sequenceLengthOverride: learningEpisodeRequest.size,
        learningEpisode: learningEpisodeRequest,
      });
      if (learningEpisodePlan && isLearningEpisodePlanReadyForStream(learningEpisodePlan, learningEpisodeRequest.size ?? activeSequenceLength)) {
        sequencePlan = learningEpisodePlan;
      } else if (learningEpisodePlan?.episode.summary) {
        unavailableReason = learningEpisodePlan.episode.summary;
        unavailableState = learningEpisodePlan.episode.availabilityState;
        unavailableCode = learningEpisodePlan.episode.emptyReasonCode;
      }
    } catch (error) {
      didSequencePlanLoadFail = true;
      unavailableReason = error instanceof Error ? error.message : '学习序列生成失败';
      unavailableState = 'service_unavailable';
      unavailableCode = 'build_failed';
    }

    if (!sequencePlan) {
      const originalUnavailableReason = unavailableReason;
      unavailableReason = buildLearningEpisodeUserFacingUnavailableReason(unavailableCode, unavailableReason);
      unavailableReason = buildLearningEpisodeFallbackDisabledMessage(
        unavailableReason || 'Learning Episode 未返回可刷卡序列。',
      );
      if (didSequencePlanLoadFail) {
        console.debug('[reviewService] loadHomeReviewSurface: no sequencePlan', {
          reason: unavailableReason,
          originalError: originalUnavailableReason,
          unavailableCode,
          request: learningEpisodeRequest,
          refreshHints: input.learningEpisodeRefreshHints,
        });
      }
    }

    const surface: ReviewHomeSurface = {
      totalPendingCount: sequencePlan?.pendingCount ?? 0,
      sequencePlan,
      modes: buildReviewModes(sequencePlan, unavailableReason),
      unavailableReason: sequencePlan ? undefined : (unavailableReason || undefined),
      unavailableState: sequencePlan ? undefined : unavailableState,
      unavailableCode: sequencePlan ? undefined : unavailableCode,
    };

    return surface;
  },

  /**
   * 换一组：结束当前 LEC session 后，按 Stream 重新 materialize Provider，并强制开启新 session。
   */
  async swapStreamLearningEpisodeSurface(input: {
    userId: string;
    subject: string;
    stream: string;
    previousPlan?: ReviewPlan | null;
    onLearningEpisodeTrace?: (trace: LearningEpisodeTraceStep[]) => void;
    onBuildingProgress?: (progress: Extract<PlanReadyApiResponse, { status: 'building' }>) => void;
  }): Promise<ReviewHomeSurface> {
    invalidateDailyReviewProgressCache(input.userId);
    if (input.previousPlan?.cacheKey) {
      clearCachedLearningEpisodeSurface(input.previousPlan.cacheKey);
    }
    if (USE_PLAN_READY_STORE) {
      const swapRefreshHints = buildSwapExcludeParticleIds(input.previousPlan);
      const swapExcludeParticleIds = swapRefreshHints?.excludeParticleIds ?? [];
      const swapResponse = await requestMaybeEnvelope<PlanReadyApiResponse>('/api/review/learning-episode-plans/swap', {
        method: 'POST',
        body: {
          userId: input.userId,
          subject: input.subject,
          stream: input.stream,
          previousSessionId: input.previousPlan?.sessionId || undefined,
          excludeParticleIds: swapExcludeParticleIds.length > 0 ? swapExcludeParticleIds : undefined,
        },
      });

      if (swapResponse.status === 'building') {
        input.onBuildingProgress?.(swapResponse);
      }

      const ready = swapResponse.status === 'ready' || swapResponse.status === 'unavailable'
        ? swapResponse
        : await pollPlanReady({
          userId: input.userId,
          subject: input.subject,
          stream: input.stream,
          preferSession: false,
          intent: 'swap',
          excludeParticleIds: swapExcludeParticleIds,
          onTrace: input.onLearningEpisodeTrace,
          onBuildingProgress: input.onBuildingProgress,
        });

      if (ready.status === 'unavailable') {
        const unavailableReason = buildLearningEpisodeUserFacingUnavailableReason(
          ready.unavailableCode || undefined,
          ready.unavailableReason || undefined,
        );
        return {
          totalPendingCount: 0,
          sequencePlan: null,
          modes: buildReviewModes(null, unavailableReason),
          unavailableReason,
          unavailableState: mapPlanReadyUnavailableState(ready.unavailableCode),
          unavailableCode: ready.unavailableCode || undefined,
        };
      }

      if (ready.status !== 'ready') {
        const unavailableReason = buildLearningEpisodeUserFacingUnavailableReason('build_failed', '学习序列生成超时');
        return {
          totalPendingCount: 0,
          sequencePlan: null,
          modes: buildReviewModes(null, unavailableReason),
          unavailableReason,
          unavailableState: 'service_unavailable',
          unavailableCode: 'build_failed',
        };
      }

      const learningEpisodeRequest = buildConfiguredLearningEpisodeRequest(input.userId, {
        subject: input.subject,
        stream: input.stream,
      });
      const cacheKey = buildLearningEpisodeCacheKey({
        userId: input.userId,
        request: learningEpisodeRequest,
      });
      const sequencePlan = await mapReadyResponseToLearningEpisodePlan({
        ready,
        request: learningEpisodeRequest,
        cacheKey,
        userId: input.userId,
      });

      if (!sequencePlan) {
        return {
          totalPendingCount: 0,
          sequencePlan: null,
          modes: buildReviewModes(null, '当前没有可用的学习序列'),
          unavailableReason: '当前没有可用的学习序列',
        };
      }

      const rawSurface: ReviewHomeSurface = {
        totalPendingCount: sequencePlan.pendingCount,
        sequencePlan,
        modes: buildReviewModes(sequencePlan),
      };

      return reviewService.ensureLearningEpisodeSessionSurface(rawSurface, { forceReplaceSession: true });
    }

    if (input.previousPlan?.sessionId) {
      try {
        await completeLearningEpisodeSession({
          sessionId: input.previousPlan.sessionId,
          completionSummary: '用户换一组，结束当前序列。',
        });
      } catch (error) {
        console.warn('[reviewService] complete session before stream swap failed:', error);
      }
    }

    const learningEpisodeRequest = buildConfiguredLearningEpisodeRequest(input.userId, {
      subject: input.subject,
      stream: input.stream,
    });
    const swapRefreshHints = buildSwapExcludeParticleIds(input.previousPlan);

    const rawSurface = await reviewService.loadHomeReviewSurface({
      userId: input.userId,
      subjectFilter: input.subject,
      stream: input.stream,
      forceRefresh: true,
      learningEpisode: learningEpisodeRequest,
      learningEpisodeRefreshHints: swapRefreshHints,
      onLearningEpisodeTrace: input.onLearningEpisodeTrace,
      onBuildingProgress: input.onBuildingProgress,
    });

    return reviewService.ensureLearningEpisodeSessionSurface(rawSurface, { forceReplaceSession: true });
  },

  async ensureLearningEpisodeSessionSurface(
    surface: ReviewHomeSurface,
    options?: { forceReplaceSession?: boolean },
  ): Promise<ReviewHomeSurface> {
    if (!surface.sequencePlan) {
      return surface;
    }

    if (surface.sequencePlan.sessionId && !options?.forceReplaceSession) {
      setCachedLearningEpisodeSurface(surface.sequencePlan.cacheKey, surface);
      return surface;
    }

    const session = await startLearningEpisodeSession(surface.sequencePlan, {
      forceReplace: options?.forceReplaceSession === true,
    });
    const nextPlan: LearningEpisodePlan = {
      ...surface.sequencePlan,
      sessionId: session.id,
      leadThoughtByParticleId: session.leadThoughtByParticleId || {},
      familyFeedbackByParticleId: session.familyFeedbackByParticleId || {},
    };

    const nextSurface = {
      ...surface,
      sequencePlan: nextPlan,
      totalPendingCount: nextPlan.pendingCount,
      modes: buildReviewModes(nextPlan),
    };

    setCachedLearningEpisodeSurface(nextPlan.cacheKey, nextSurface);
    return nextSurface;
  },

  async persistReviewSequenceCursor(plan: ReviewPlan, nextIndex: number) {
    const safeIndex = Math.max(0, nextIndex);
    const nextParticleIds = plan.particles.map((particle) => particle.id);
    const remainingParticles = nextParticleIds.slice(safeIndex);
    const nextSurface: ReviewHomeSurface = {
      totalPendingCount: remainingParticles.length,
      sequencePlan: {
        ...plan,
        particles: plan.particles.filter((particle) => remainingParticles.includes(particle.id)),
        currentIndex: 0,
        pendingCount: remainingParticles.length,
      },
      modes: buildReviewModes({
        ...plan,
        particles: plan.particles.filter((particle) => remainingParticles.includes(particle.id)),
        currentIndex: 0,
        pendingCount: remainingParticles.length,
      }),
    };
    setCachedLearningEpisodeSurface(plan.cacheKey, nextSurface);
  },

  async persistReviewSequenceProgress(
    plan: ReviewPlan,
    progress: {
      reviewCursor?: string;
      reviewedCount: number;
      totalCount?: number;
      particleIds?: string[];
      deckCursor?: LECDeckCursor | null;
    },
  ) {
    const remainingIds = Array.isArray(progress.particleIds)
      ? progress.particleIds.filter(Boolean)
      : plan.particles.map((particle) => particle.id);
    const remainingParticles = plan.particles.filter((particle) => remainingIds.includes(particle.id));
    const nextDeckCursor = progress.deckCursor ?? plan.deckCursor ?? null;
    if (plan.sessionId) {
      await updateLearningEpisodeSessionProgress({
        sessionId: plan.sessionId,
        particleIds: remainingIds,
        deckCursor: nextDeckCursor ?? undefined,
      });
    }
    const nextPlan: LearningEpisodePlan = {
      ...plan,
      particles: remainingParticles,
      currentIndex: 0,
      pendingCount: remainingParticles.length,
      totalCount: Math.max(progress.totalCount ?? plan.totalCount, remainingParticles.length),
      familyFeedbackByParticleId: plan.familyFeedbackByParticleId,
      deckCursor: nextDeckCursor,
    };
    setCachedLearningEpisodeSurface(plan.cacheKey, {
      totalPendingCount: remainingParticles.length,
      sequencePlan: nextPlan,
      modes: buildReviewModes(nextPlan),
    });
  },

  clearLearningEpisodeSurface(cacheKey: string) {
    clearCachedLearningEpisodeSurface(cacheKey);
  },

  async recordReviewFeedback(
    plan: ReviewPlan,
    particleId: string,
    feedback: ReviewFeedbackKind,
    options?: {
      questionShownAt?: Date | null;
      answerShownAt?: Date | null;
      feedbackAt?: Date;
    },
  ) {
    if (!plan.sessionId) {
      return null;
    }

    return updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      reviewedParticleId: particleId,
      reviewedAt: (options?.feedbackAt || new Date()).toISOString(),
      feedback,
    });
  },

  async recordFamilyOverviewCompleted(plan: ReviewPlan, input: {
    particleId: string;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
    completedAt?: string;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    invalidateDailyReviewProgressCache(readUserIdFromPlanCacheKey(plan.cacheKey));
    const completedAt = input.completedAt || new Date().toISOString();
    return updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      familyCompletion: {
        particleId: input.particleId,
        completedAt,
        feedback: null,
        cardKind: input.cardKind || 'full-answer',
        streamDecorationKind: input.streamDecorationKind,
      },
    });
  },

  async completeFamilyReview(plan: ReviewPlan, input: {
    particleId: string;
    feedback?: ReviewFeedbackKind | null;
    completedAt?: string;
    summaryScore?: number | null;
    summaryText?: string | null;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    invalidateDailyReviewProgressCache(readUserIdFromPlanCacheKey(plan.cacheKey));
    const completedAt = input.completedAt || new Date().toISOString();
    return updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      ...(input.feedback ? {
        reviewedParticleId: input.particleId,
        reviewedAt: completedAt,
        feedback: input.feedback,
      } : {}),
      familyCompletion: {
        particleId: input.particleId,
        completedAt,
        feedback: input.feedback ?? null,
        summaryScore: input.summaryScore,
        summaryText: input.summaryText,
        cardKind: input.cardKind,
        streamDecorationKind: input.streamDecorationKind,
      },
    });
  },

  async loadFamilyFootprint(userId: string, options?: { limit?: number; offset?: number; subject?: string; search?: string }) {
    const params = new URLSearchParams({
      userId,
      limit: String(options?.limit ?? 50),
      offset: String(options?.offset ?? 0),
    });
    if (options?.subject) {
      params.set('subject', options.subject);
    }
    if (options?.search) {
      params.set('search', options.search);
    }

    return requestMaybeEnvelope<LearningEpisodeFamilyFootprintSnapshot>(
      `/api/review/learning-episodes/families/footprint?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async loadRazBookmarks(userId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams({
      userId,
      limit: String(options?.limit ?? 50),
      offset: String(options?.offset ?? 0),
    });

    return requestMaybeEnvelope<LearningEpisodeRazBookmarkSnapshot>(
      `/api/review/learning-episodes/raz-bookmarks?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async syncRazBookmark(input: {
    userId: string;
    cardId: string;
    active: boolean;
    sessionId?: string | null;
    particleId: string;
    title?: string;
    questionMain?: string;
    topicKey?: string;
    topicLabel?: string;
    stream: string;
    subject: string;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
    familyIndex?: number | null;
    familyCardIndex?: number | null;
    familyCardCount?: number | null;
    occurredAt?: string;
  }) {
    await requestMaybeEnvelope<void>(
      '/api/review/learning-episodes/raz-bookmarks/sync',
      {
        method: 'POST',
        body: input,
      },
    );
  },

  async loadDifficultyAnnotations(userId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams({
      userId,
      limit: String(options?.limit ?? 50),
      offset: String(options?.offset ?? 0),
    });

    return requestMaybeEnvelope<LearningEpisodeDifficultyAnnotationSnapshot>(
      `/api/review/learning-episodes/difficulty-annotations?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async loadQualityFeedback(userId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams({
      userId,
      limit: String(options?.limit ?? 50),
      offset: String(options?.offset ?? 0),
    });

    return requestMaybeEnvelope<LearningEpisodeQualityFeedbackSnapshot>(
      `/api/review/learning-episodes/quality-feedback?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async loadLearningPositionSubjects(userId: string) {
    const params = new URLSearchParams({ userId });
    return requestMaybeEnvelope<LearningPositionSubjectSummary[]>(
      `/api/review/learning-position/subjects?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async loadLearningPositionSpineGraph(userId: string, subject: string) {
    const params = new URLSearchParams({ userId, subject });
    return requestMaybeEnvelope<LearningPositionSpineGraph>(
      `/api/review/learning-position/spine-graph?${params.toString()}`,
      { method: 'GET' },
    );
  },

  /** Aggregated trunk coverage counts for histobar UI (no family payload). */
  async loadLearningPositionSpineCoverage(userId: string, subject: string) {
    const params = new URLSearchParams({ userId, subject });
    return requestMaybeEnvelope<LearningPositionSpineCoverageSummary>(
      `/api/review/learning-position/spine-coverage?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async loadLearningPositionSpineCoverageParticles(
    userId: string,
    subject: string,
    nodeId: string,
    options?: { page?: number; pageSize?: number },
  ) {
    const params = new URLSearchParams({
      userId,
      subject,
      nodeId,
      page: String(Math.max(1, options?.page ?? 1)),
      pageSize: String(Math.min(100, Math.max(1, options?.pageSize ?? 20))),
    });
    return requestMaybeEnvelope<LearningPositionSpineCoverageParticlePage>(
      `/api/review/learning-position/spine-coverage/particles?${params.toString()}`,
      { method: 'GET' },
    );
  },

  async completeLearningEpisodeSession(plan: ReviewPlan, input?: {
    completionScore?: number | null;
    completionFeedback?: string | null;
    completionSummary?: string | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    return completeLearningEpisodeSession({
      sessionId: plan.sessionId,
      completionScore: input?.completionScore,
      completionFeedback: input?.completionFeedback,
      completionSummary: input?.completionSummary,
    });
  },

  async saveLearningEpisodeFamilyFeedback(plan: ReviewPlan, input: {
    particleId: string;
    score?: number | null;
    feedback?: string | null;
    printSubQuestionsExpanded?: boolean | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    const session = await updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      familyFeedback: {
        particleId: input.particleId,
        score: input.score,
        feedback: input.feedback,
        submittedAt: new Date().toISOString(),
        printSubQuestionsExpanded: input.printSubQuestionsExpanded,
      },
    });

    if (!session) {
      return session;
    }

    const nextFamilyFeedback = mergeFamilyFeedbackByParticleId(
      plan.familyFeedbackByParticleId,
      session.familyFeedbackByParticleId,
      input.particleId,
      {
        score: session.familyFeedbackByParticleId?.[input.particleId]?.score ?? input.score,
        feedback: session.familyFeedbackByParticleId?.[input.particleId]?.feedback ?? input.feedback,
        submittedAt: session.familyFeedbackByParticleId?.[input.particleId]?.submittedAt || new Date().toISOString(),
        printSubQuestionsExpanded: session.familyFeedbackByParticleId?.[input.particleId]?.printSubQuestionsExpanded ?? input.printSubQuestionsExpanded,
      },
    );
    const nextPlan: LearningEpisodePlan = {
      ...plan,
      familyFeedbackByParticleId: nextFamilyFeedback,
    };

    // Recompute pending particles excluding any that now have a score entry so the
    // cached surface immediately reflects the correct badge count for HomeScreen.
    // We use score presence or feedback presence (not just submittedAt) as the "reviewed" indicator
    // because print-toggle saves also set submittedAt but carry no score.
    const cachedFamilyFeedback = nextFamilyFeedback as LearningEpisodePlan['familyFeedbackByParticleId'];
    const cachedSubmittedIds = new Set(
      Object.entries(cachedFamilyFeedback)
        .filter(([, entry]) => typeof entry.score === 'number' || Boolean(entry.feedback?.trim()))
        .map(([id]) => id),
    );
    const cachedPendingParticles = cachedSubmittedIds.size > 0
      ? nextPlan.particles.filter((p) => !cachedSubmittedIds.has(p.id))
      : nextPlan.particles;
    const nextPlanForCache: LearningEpisodePlan = {
      ...nextPlan,
      particles: cachedPendingParticles,
      pendingCount: cachedPendingParticles.length,
    };
    setCachedLearningEpisodeSurface(plan.cacheKey, {
      totalPendingCount: cachedPendingParticles.length,
      sequencePlan: nextPlanForCache,
      modes: buildReviewModes(nextPlanForCache),
    });

    return session;
  },

  async saveLearningEpisodeLeadThought(plan: ReviewPlan, input: {
    particleId: string;
    text: string;
    inputMode?: LECLeadThoughtInputMode | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    const session = await updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      leadThought: {
        particleId: input.particleId,
        text: input.text,
        updatedAt: new Date().toISOString(),
        inputMode: input.inputMode,
      },
    });

    if (!session) {
      return session;
    }

    const nextLeadThought = mergeLeadThoughtByParticleId(
      plan.leadThoughtByParticleId,
      session.leadThoughtByParticleId,
      input.particleId,
      {
        text: input.text,
        updatedAt: session.leadThoughtByParticleId?.[input.particleId]?.updatedAt || new Date().toISOString(),
        inputMode: session.leadThoughtByParticleId?.[input.particleId]?.inputMode ?? input.inputMode,
      },
    );
    const nextPlan: LearningEpisodePlan = {
      ...plan,
      leadThoughtByParticleId: nextLeadThought,
    };

    setCachedLearningEpisodeSurface(plan.cacheKey, {
      totalPendingCount: nextPlan.pendingCount,
      sequencePlan: nextPlan,
      modes: buildReviewModes(nextPlan),
    });

    return session;
  },

  async saveLearningEpisodeSubCardFeedback(plan: ReviewPlan, input: {
    particleId: string;
    cardFeedbacks: Array<{ cardId: string; rating: ReviewFeedbackKind }>;
    submittedAt?: string | null;
    clientRequestId?: string | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    return updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      subCardFeedback: {
        particleId: input.particleId,
        cardFeedbacks: input.cardFeedbacks,
        submittedAt: input.submittedAt || new Date().toISOString(),
        clientRequestId: input.clientRequestId,
      },
    });
  },

  async recordLearningEpisodeFamilyPresented(plan: ReviewPlan, input: {
    particleId: string;
    cardId?: string | null;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
    familyIndex?: number | null;
    familyCardIndex?: number | null;
    occurredAt?: string | null;
    clientRequestId?: string | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    const deckCursor = buildLECDeckCursorFromCard({
      id: String(input.cardId || ''),
      particleId: input.particleId,
      familyCardIndex: typeof input.familyCardIndex === 'number' ? input.familyCardIndex : undefined,
    });

    return updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      deckCursor: deckCursor ?? undefined,
      familyPresentation: {
        particleId: input.particleId,
        cardId: input.cardId,
        cardKind: input.cardKind,
        streamDecorationKind: input.streamDecorationKind,
        familyIndex: input.familyIndex,
        familyCardIndex: input.familyCardIndex,
        occurredAt: input.occurredAt || new Date().toISOString(),
        clientRequestId: input.clientRequestId,
      },
    });
  },

  async saveLearningEpisodeDeckCursor(plan: ReviewPlan, input: {
    particleId: string;
    cardId: string;
    familyCardIndex?: number | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    const deckCursor = buildLECDeckCursorFromCard({
      id: input.cardId,
      particleId: input.particleId,
      familyCardIndex: typeof input.familyCardIndex === 'number' ? input.familyCardIndex : undefined,
    });
    if (!deckCursor) {
      return null;
    }

    const session = await updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      deckCursor,
    });

    if (!session) {
      return session;
    }

    const nextPlan: LearningEpisodePlan = {
      ...plan,
      deckCursor: session.deckCursor ?? deckCursor,
    };
    setCachedLearningEpisodeSurface(plan.cacheKey, {
      totalPendingCount: nextPlan.pendingCount,
      sequencePlan: nextPlan,
      modes: buildReviewModes(nextPlan),
    });
    return session;
  },

  async recordLearningEpisodeFamilyAction(plan: ReviewPlan, input: {
    particleId: string;
    cardKind?: string | null;
    streamDecorationKind?: string | null;
    actionIntent: string;
    intentClass: ReviewLongPressIntentClass;
    qualityReasonCodes?: string[] | null;
    qualityReasonLabel?: string | null;
    qualityReasonDescription?: string | null;
    noteText?: string | null;
    occurredAt?: string | null;
    clientRequestId?: string | null;
  }) {
    if (!plan.sessionId) {
      return null;
    }

    return updateLearningEpisodeSessionProgress({
      sessionId: plan.sessionId,
      familyAction: {
        particleId: input.particleId,
        cardKind: input.cardKind,
        streamDecorationKind: input.streamDecorationKind,
        actionIntent: input.actionIntent,
        intentClass: input.intentClass,
        qualityReasonCodes: input.qualityReasonCodes,
        qualityReasonLabel: input.qualityReasonLabel,
        qualityReasonDescription: input.qualityReasonDescription,
        noteText: input.noteText,
        occurredAt: input.occurredAt || new Date().toISOString(),
        clientRequestId: input.clientRequestId,
      },
    });
  },

  async loadSessionHistory(userId: string, options?: { limit?: number; subject?: string; forceRefresh?: boolean }) {
    const normalizedUserId = String(userId || '').trim();
    if (!normalizedUserId) {
      return [];
    }

    const limit = options?.limit ?? 60;
    const subjectFilter = options?.subject?.trim() || undefined;

    if (!options?.forceRefresh) {
      const cached = getCachedSessionHistory(normalizedUserId, limit, subjectFilter);
      if (cached) {
        return cached;
      }
    }

    const [apiHistory, activeEntries] = await Promise.all([
      requestMaybeEnvelope<LearningEpisodeSessionHistoryEntry[]>(
        `/api/review/learning-episodes/sessions/history?${new URLSearchParams({
          userId: normalizedUserId,
          limit: String(limit),
          ...(subjectFilter ? { subject: subjectFilter } : {}),
        }).toString()}`,
        { method: 'GET' },
      ).catch(() => [] as LearningEpisodeSessionHistoryEntry[]),
      loadActiveSessionHistoryEntries(normalizedUserId, subjectFilter),
    ]);

    const normalizedApi = (Array.isArray(apiHistory) ? apiHistory : []).map(normalizeSessionHistoryEntry);
    const activeSessionIds = new Set(activeEntries.map((entry) => entry.sessionId));

    const merged = [
      ...activeEntries,
      ...normalizedApi.filter((entry) => !activeSessionIds.has(entry.sessionId)),
    ].sort((left, right) => {
      const leftTime = new Date(left.completedAt || left.startedAt).getTime();
      const rightTime = new Date(right.completedAt || right.startedAt).getTime();
      return rightTime - leftTime;
    });

    const sliced = merged.slice(0, limit);
    setCachedSessionHistory(normalizedUserId, limit, sliced, subjectFilter);
    return sliced;
  },

  async getKnowledgeParticleReviewHistory(particleId: string, userId: string) {
    const params = new URLSearchParams({ userId });
    return requestMaybeEnvelope<LearningEpisodeParticleHistoryEntry[]>(
      `/api/review/learning-episodes/particles/${encodeURIComponent(particleId)}/history?${params.toString()}`,
      { method: 'GET' },
    );
  },

  /**
   * Drop in-memory review caches when auth session ends.
   * Same-device multi-account switches must not reuse another user's history/progress.
   */
  clearCachesOnSignedOut() {
    invalidateDailyReviewProgressCache();
    learningEpisodeSurfaceCache.clear();
    learningSequenceSettingsByUserId.clear();
    lastReviewProductDayByUserId.clear();
    streamPrewarmAtByKey.clear();
    siblingPrewarmAtByKey.clear();
  },
};